// AI Product Recommender (WAT-11276) — recipe-driven questionnaire + deterministic
// recommendation. The question count/branching lives entirely in the CMS recipe;
// this component never assumes how many questions exist.
//
// Entry mode (CMS-configurable, `entry_mode` blok field — default "modal"):
//   "modal"  (A, default) — a banner ("Not sure which TV package…" + Get started)
//                           that opens the quiz + results inside a DtvModal.
//   "inline" (C)          — the SAME banner, whose CTA swaps a quiz card into the
//                           page in place (no overlay); results render in that card.
// Both shells share ONE engine, question step, result card, analytics, entry banner
// and sessionStorage — only the container differs. The banner-first inline shell is the
// adopted design (Figma 7190:841 / 7190:1120); it supersedes the earlier "first question
// directly on the page, no click" description of inline.
//
// Contract with the server adapters (recipe-adapter / catalog-adapter):
// props arrive pre-adapted. Any 'error' in `messages` (or a missing catalog)
// means the config/catalog is unusable -> render nothing (never a broken quiz,
// never a card without a live price).
//
// Session persistence (AC-2g): sessionStorage only, keyed by blok _uids so a
// CMS reorder cannot misattribute stored answers.

import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import Button from "@directv-digital/fusion.atoms.button";
// Static, like the app's other content modals (dtv-horizontal-plan-card-module.tsx).
// Both consumers already dynamic-import this whole component (WnCMainCanvas.tsx:198,
// app/.../component-map.tsx:263), so the modal is out of the initial bundle either way;
// a second split inside it would buy an unmeasured amount and force an async boundary.
import DtvModal from "@directv-digital/dds.ui.dtv-modal";
import { fireGa4ItemList } from "@directv-digital/dds.services.analytics-service";
import {
  colorPalette,
  MEDIAQUERY_DESKTOP,
  MEDIAQUERY_LARGE_DESKTOP,
  MOBILE_WIDTH
} from "@directv-digital/dds.ui.dtv-theme";
import FusionTheme from "@directv-digital/fusion.themes.fusion";
import { run, visibleQuestions } from "./engine/engine";
import { Answers, Catalog, Recipe, RecipeQuestion, RunResult } from "./engine/engine.types";
import { PricedCatalog, PricedCatalogPackage, ResolvedPriceTier, secondaryCtaUrl } from "./adapters/catalog-adapter";
import { getMultiOfferDuration } from "../plan-cards/utils/dynamicPrices";
import { RecipeValidationMessage } from "./engine/validate-recipe";
import { buildBroadCtaUrl, buildFocusedCtaUrl } from "./utils/build-cta";
import {
  absoluteCtaUrl,
  broadItemId,
  currentPageUrl,
  fireQuizPageView,
  fireQuizQuestionStatus,
  fireQuizRecommendationStatus,
  fireQuizStatus,
  focusedItemId,
  LinkClickAttrs,
  linkClickAttrs,
  navLinkPosition,
  optionLinkPosition,
  quizLinkPosition,
  quizVirtualUrl,
  recommendationLinkPosition
} from "./utils/analytics";

export type AiRecommenderEntryMode = "modal" | "inline";

export type AiProductRecommenderProps = {
  id?: string;
  /** Entry-point variant (CMS `entry_mode`). Default "modal". */
  entryMode?: AiRecommenderEntryMode;
  heading?: string;
  subhead?: string;
  entryImage?: string;
  entryCtaLabel?: string;
  /**
   * Entry-banner copy cap in percent (CMS `entry_copy_max_width_pct`), applied only while
   * `entryImage` is set. Exists because the right value depends on where the subject sits
   * in whichever photo the CMS is serving. Unset/invalid -> ENTRY_COPY_MAX_PCT_DEFAULT.
   */
  entryCopyMaxWidthPct?: number | null;
  resultsHeading?: string;
  nextLabel?: string;
  backLabel?: string;
  closeLabel?: string;
  /** Helper under a single-select question title (Figma: "Select one"). */
  selectOneLabel?: string;
  /** Helper under a multi-select question title (Figma: "Select all that apply"). */
  selectAllLabel?: string;
  resultsCtaLabel?: string;
  shopCtaLabel?: string;
  primaryTagLabel?: string;
  startOverLabel?: string;
  priceLegal?: string;
  /**
   * Price-disclosure templates from the CMS. One object rather than three props on
   * purpose: they are identical for every card, and ResultCard enumerates its props
   * explicitly, so three separate ones would be three more places to forget to thread
   * (already the cause of one bug where every card rendered "+ tax"). Any field left
   * blank, or missing its token, falls back to the built-in wording.
   */
  priceCopy?: AiRecommenderPriceCopy;
  recipe?: Recipe;
  /**
   * PricedCatalog is Catalog plus the display-only promo fields, all optional — so a
   * plain Catalog still satisfies this and simply renders no promo disclosure. Declared
   * as the priced type (which adaptCatalog actually returns) rather than a union with
   * Catalog: the union would collapse straight back to Catalog and buy nothing.
   */
  catalog?: PricedCatalog | null;
  messages?: RecipeValidationMessage[];
  backgroundColor?: string;
  /**
   * In-page only: the second results CTA, authored per pack because the signature card and the
   * genre card point at different lineups (CMS `secondary_cta_signature_*` / `secondary_cta_genre_*`).
   * The url is the gate — no url for a pack means no second button on that pack's card; a blank
   * label falls back to the built-in "See all packages".
   */
  secondaryCtaSignatureLabel?: string;
  secondaryCtaSignatureUrl?: string | null;
  secondaryCtaGenreLabel?: string;
  secondaryCtaGenreUrl?: string | null;
};

// Design breakpoint: >=992px right-anchors the uncropped banner image with a left-edge
// mask fade; below it the image covers the banner full-bleed behind the copy (no mask).
// The px is its own constant because ENTRY_BANNER_SIZES needs the same number in a plain
// media condition rather than an sx key, and two spellings of one breakpoint drift.
const DESKTOP_MIN_PX = 992;
export const DESKTOP_MQ = `@media (min-width: ${DESKTOP_MIN_PX}px)`;
const BELOW_DESKTOP_MQ = `@media (max-width: ${DESKTOP_MIN_PX - 1}px)`;
// In-page card breakpoint. Raw, for the same reason DESKTOP_MQ is — the DDS theme has no
// 768px stop — and split in two because the answers cap needs the bare form for matchMedia.
const INPAGE_TABLET_MIN_PX = 768;
const INPAGE_TABLET_QUERY = `(min-width: ${INPAGE_TABLET_MIN_PX}px)`;
export const INPAGE_TABLET_MQ = `@media ${INPAGE_TABLET_QUERY}`;
// Widest the entry-banner copy may get once the banner is narrow enough that the photo's
// subject is in the way. The right value depends entirely on WHERE the subject sits in
// whatever asset the CMS is serving, so it is authorable (`entry_copy_max_width_pct`) —
// swapping the photo is a CMS action and should not need a code change to follow it.
// This constant is the fallback AND the value the Storyblok field defaults to; the two
// must stay equal or an unset field renders differently from a freshly-created one.
// 70 was measured against the 2026-07-31 asset — see the note at the EntryBanner heading.
export const ENTRY_COPY_MAX_PCT_DEFAULT = 70;
// The flat fill the right-anchored desktop photo fades into. Measured off the
// 2026-08-05 DAM asset (1x1 resample of its backdrop) — like the copy cap above, the
// right value belongs to the asset, so re-sample if the entry image is swapped.
const ENTRY_BANNER_BACKDROP = "#dde1ea";
// DtvModal sits above the header/nav (matches the app's other content modals).
const MODAL_ROOT_SX = { zIndex: 1303 };

// Figma 6976:2185: the quiz modal card is 578px wide on desktop. DtvModal's
// modalStyle/modalBodyStyle spread over the card / scroll-body sx as whole-key
// overrides, so each key we set replaces DtvModal's entire responsive object.
// We deliberately key ONLY xs + sm: MUI cascades sm up to md/lg/xl, so a single
// sm value covers every desktop breakpoint. That sidesteps DtvModal's stepped
// caps, whose md/lg thresholds don't line up with the DTV theme's breakpoints
// (md body cap = 480px, shorter than a short quiz step, so Q1 scrolled on
// common laptop widths like 1366). xs preserves the mobile bottom sheet.
const MODAL_CARD_SX = {
  width: "100%",
  maxWidth: {
    xs: "100%",
    sm: "578px"
  },
  // vh base, dvh upgrade. An unsupported unit inside min() invalidates the whole value,
  // so on a browser without dvh (iOS Safari < 15.4) a dvh-only rule is DROPPED — and
  // because modalStyle replaces DtvModal's own cap whole-key, nothing would be left to
  // fall back on. Verified the sx output nests both breakpoints inside the @supports.
  maxHeight: {
    xs: "min(700px, 100vh)",
    sm: "min(820px, calc(100vh - 24px))"
  },
  "@supports (height: 100dvh)": {
    maxHeight: {
      xs: "min(700px, 100dvh)",
      sm: "min(820px, calc(100dvh - 24px))"
    }
  },
  // Figma 6976:2255 card radius is 12px; DtvModal defaults to 10px.
  // xs is a bottom sheet: DtvModal drops the centring transform and pins the card
  // to the viewport floor (measured at 500x740 — card bottom 740, gap 0), so the
  // lower corners were rounding against the screen edge with nothing behind them.
  // Square them there; sm and up the card is centred with clearance on all four
  // sides, so it keeps the full Figma radius.
  borderRadius: {
    xs: "12px 12px 0 0",
    sm: "12px"
  }
};

// DDS thumb fill, copied from fusionScrollBarStyles
// (dds.core.commons/dist/CommonsService.js, thumb backgroundColor) so the thumb
// keeps the exact DDS colour rather than inventing a near-match.
const SCROLLBAR_THUMB_COLOR = "#DCDEE3";

// DtvModal's own inset on the scroll body: it sets ml AND mr to this same object, so
// the content sits symmetrically inside the card. Mirrored here because the scrollbar
// fix below splits the right-hand value into margin + padding and has to add back up
// to it. DtvModal's md term is `isChlupModal ? 48 : 56`, and isChlupModal is derived
// from a `modalUrl` containing "channel-lineup/modal" — a prop this component never
// passes — so 56 is the only value this call site can ever see.
const BODY_INSET_PX = {
  xs: 20,
  sm: 32,
  md: 56
};
// How far the scrollbar should sit from the card edge. The padding is derived from it,
// so changing this one number keeps mr + pr == ml instead of desyncing the two sides.
const BODY_SCROLL_GUTTER_PX = 8;
const toPx = (n: number) => `${n}px`;
// One value, not a responsive object: modalBodyStyle replaces DtvModal's `mr` whole-key,
// and the gutter is the same at every width. `pr` does need all three because its values
// differ; lg/xl inherit md through MUI's cascade.
const BODY_SCROLL_MR = toPx(BODY_SCROLL_GUTTER_PX);
const BODY_SCROLL_PR = {
  xs: toPx(BODY_INSET_PX.xs - BODY_SCROLL_GUTTER_PX),
  sm: toPx(BODY_INSET_PX.sm - BODY_SCROLL_GUTTER_PX),
  md: toPx(BODY_INSET_PX.md - BODY_SCROLL_GUTTER_PX)
};

// Body cap 700px fits a Figma-spaced short step (Q1 content ≈ 665px with the
// 16/30 option padding + 19px gaps) scroll-free on any window ≳ 800px tall;
// taller content (Q2's 6 options, the results cards) still overflows and
// scrolls. The 100dvh term keeps it inside short viewports instead of clipping
// under the fold. In the ask phase the scrolling happens one level in — see
// MODAL_BODY_SX_ASK, which spreads this and turns the body itself into a column.
const MODAL_BODY_SX = {
  maxHeight: {
    xs: "calc(min(700px, 100vh) - 120px)",
    sm: "min(700px, calc(100vh - 136px))"
  },
  "@supports (height: 100dvh)": {
    maxHeight: {
      xs: "calc(min(700px, 100dvh) - 120px)",
      sm: "min(700px, calc(100dvh - 136px))"
    }
  },
  // --- scrollbar visibility -------------------------------------------------
  // DDS calls fusionScrollBarStyles (dds.core.commons/dist/CommonsService.js:76)
  // on this box, which sets `::-webkit-scrollbar { width: 20px }`. Styling that
  // pseudo-element at all opts the element OUT of the platform's overlay
  // scrollbar and into a CLASSIC one, which Chrome paints for as long as the
  // element overflows — that is the whole reason the bar never went away.
  // Measured on the real modal: offsetWidth - clientWidth was 20px while
  // overflowing (0px when not), i.e. exactly DDS's width, not Chrome's own 15.
  //
  // Restyling the webkit pseudo-elements cannot fix this. Chrome resolves
  // ::-webkit-scrollbar-* ONCE per element and never recomputes it: an injected
  // `background-color: red !important` on a plain class did not repaint the
  // thumb, and neither did :hover, nor scrolling the box. A CSS hover-reveal is
  // therefore dead code here.
  //
  // The standard properties DO win over the webkit rule and hand the element
  // back to the platform: with `scrollbar-width` set, the same box measured 0
  // layout px — identical to a control div with no scrollbar styling at all. On
  // macOS that means the auto-hiding overlay bar is back (verified on screen:
  // invisible at rest, visible while scrolling). scrollbar-color keeps the DDS
  // fill so it still looks like the rest of the design system.
  //
  // Measured both engines on macOS with a probe div carrying the same DDS rule,
  // reading offsetWidth - clientWidth (0 = platform overlay bar):
  //
  //   combination                      Chrome   Firefox
  //   no scrollbar CSS (control)          0        0
  //   DDS webkit rule alone (before)     20       15
  //   scrollbar-color alone               0       15
  //   scrollbar-width:thin  (+color)      0       11   <- shipped
  //
  // So Firefox was ALREADY non-overlay before this change: its 15px comes from the
  // DDS webkit rule, which Firefox honours far enough to leave overlay mode. No
  // combination of standard properties wins overlay back there while that rule is
  // present, so 11px is the floor — narrower than today's 15px and tinted to DDS.
  // scrollbar-color costs nothing in layout in either engine; it only sets the fill.
  // Windows/Linux have no overlay bar at all, so they keep a thin classic one by
  // platform convention. Chrome is the only engine where the bar actually vanishes.
  scrollbarWidth: "thin",
  scrollbarColor: `${SCROLLBAR_THUMB_COLOR} transparent`,
  // --- scrollbar position -----------------------------------------------------
  // DtvModal gives the scroll body ml AND mr of 20/32/56px, so the bar floated far
  // inside the card — measured at 1440x760, the card's right edge was 1009 and the
  // scroll container's was 953. Pull the container out to the gutter and hand the
  // removed space back as padding, so the CONTENT stays put:
  //   content right gap = mr + pr  (the overlay bar costs 0 layout px)
  // Both values are derived from BODY_INSET_PX above, so mr + pr == ml holds by
  // construction rather than by three hand-checked literals.
  //
  // This also removes a wart: with the old classic 20px bar the content shifted
  // 56px -> 76px the moment a step overflowed, so the body was asymmetric exactly
  // when it scrolled. At 0 layout px the gap is now the same in both states.
  //
  // On a browser that ignores `scrollbar-width` (Safari < 18.2) the classic bar
  // survives, but this split is still a no-op there, because the bar sits BETWEEN
  // the padding edge and the margin — content-to-card-edge is mr + bar + pr, and
  // mr + pr is exactly the old mr at every breakpoint. Measured with a probe div
  // carrying the same 20px webkit rule and no standard properties: before/after
  // gaps were 40/40 (xs), 52/52 (sm), 76/76 (md). So the fallback is unchanged,
  // not degraded.
  //
  // Both keys are whole-key overrides of DtvModal's own responsive objects, not merges
  // — so anything they omit is dropped rather than inherited from DtvModal.
  mr: BODY_SCROLL_MR,
  pr: BODY_SCROLL_PR
};

// --- ask phase: the scroll moves one level in --------------------------------------
// The CTA row used to be `position: sticky` INSIDE this body, which means it necessarily
// painted over whatever was still scrolling underneath. Measured at 390x844 on question 2:
// 122px of options — two whole rows — sat behind an opaque bar, and the bar's top edge landed
// on the exact pixel where the next option began. The overlap is
// (last option's bottom) - (scroll-body bottom - footer height), so it is a property of
// putting a bar inside the box it pins over, not a number that can be tuned away (verified:
// +24px of padding under the options made it worse, 8 -> 32, because the extra height is all
// extra scroll).
//
// So for the ask phase the body hands its scrolling to an inner region and becomes a plain
// column (it keeps `overflow-y: auto`, but the flex math below means it never uses it except
// in the degenerate case described further down): the question content
// scrolls in its own region (STEP_SCROLL_SX) and the CTA row is a plain flex sibling below it.
// Nothing is ever covered. It also un-blocks the pure-CSS scroll shadow, which cannot work
// while an opaque bar sits on the scrolling box's own bottom edge (verified: the shadow band
// painted at y 798-812, entirely inside the bar's box, and nothing was visible).
//
// Results keeps the plain scrolling body — it has no footer to pin.
// overflowY is deliberately NOT set here, which leaves DtvModal's own `overflowY: auto` on
// the body standing (dtv-modal.js:132-146 spreads modalBodyStyle after it, which is exactly
// why an override would win). An earlier revision pinned it to `hidden`, reasoning that the
// body must not become a second scroller. It does not need to be told: the region is
// `1 1 auto` with `minHeight: 0` and the CTA row is the only item that cannot shrink, so the
// region absorbs every pixel of slack and the body's content height equals its own by
// construction. Measured at 500x500, the tightest ordinary case: region scrollHeight 396 vs
// clientHeight 204 (it scrolls), body 380 vs 380 (it does not). Two scrollbars is not a
// reachable state — the body can only overflow once the region is already at 0.
//
// What `hidden` DID add is a trap at the far end. Below roughly 290px of viewport the region
// hits 0 and the CTA row itself no longer fits: measured, the body overflowed by 36px at
// 260px of viewport and by 64px at 232px, and because the row stacks the buttons the part
// that falls outside is the BOTTOM of the row — the primary CTA. `hidden` clips that with no
// way to scroll to it; `auto` leaves the one degenerate case reachable.
//
// Note the row only stacks at `xs`, i.e. under 600px WIDE (see STEP_FOOTER_BASE_SX), so the
// case is a window narrow AND short at once — a resized desktop window, not a landscape
// phone. A landscape phone is 600px+ wide, lands on `sm`, and keeps the buttons in a row,
// which is half the height and correspondingly harder to trap.
//
// It costs nothing the rest of the time: offsetWidth - clientWidth was 0 at every viewport
// measured (844, 500, 260, 232), overflowing or not.
//
// Careful when testing this: `overflow: hidden` still scrolls PROGRAMMATICALLY, so assigning
// scrollTop reveals the row under both values and proves nothing. Only a real wheel/touch
// gesture, or the presence of a scrollbar, tells them apart.
const MODAL_BODY_SX_ASK = {
  ...MODAL_BODY_SX,
  display: "flex",
  flexDirection: "column",
  // The gutter split moves to the scroll region below, so the body itself keeps only the
  // margin. Leaving `pr` here would inset the region and push its scrollbar off the gutter.
  pr: 0
};

// The modal children wrapper carried 4px of horizontal padding. With the scroll one level in,
// that padding has to sit INSIDE the scroll region (and on the CTA row) instead, or the
// scrollbar ends up 4px off the gutter BODY_SCROLL_MR deliberately aligned it to.
const STEP_H_PAD_PX = 4;
const STEP_H_PAD = toPx(STEP_H_PAD_PX);
const STEP_PR = {
  xs: toPx(BODY_INSET_PX.xs - BODY_SCROLL_GUTTER_PX + STEP_H_PAD_PX),
  sm: toPx(BODY_INSET_PX.sm - BODY_SCROLL_GUTTER_PX + STEP_H_PAD_PX),
  md: toPx(BODY_INSET_PX.md - BODY_SCROLL_GUTTER_PX + STEP_H_PAD_PX)
};

// No scroll-edge cue here on purpose. A pure-CSS scroll shadow (`background-attachment: local`
// cover gradients over `scroll` shadow gradients) was built and measured — it costs nothing,
// runs no JS, and correctly shows nothing when the region does not overflow — and BK looked at
// it and asked for it out. Do not reintroduce one without asking. If it ever comes back, note
// that it is only possible because the CTA row now sits OUTSIDE this region; while the row was
// a sticky bar inside it, the shadow painted underneath the bar and was invisible.

// Modal only. `minHeight: 0` on every link of the chain is load-bearing: a column flex item
// defaults to min-height:auto, which refuses to shrink below its content, so without it the
// region grows past the body's max-height and nothing scrolls at all.
const MODAL_CONTENT_ASK_SX = {
  padding: "8px 0 0",
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  minHeight: 0
};
const MODAL_CONTENT_RESULTS_SX = { padding: "8px 4px" };
const STEP_ROOT_MODAL_SX = {
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  minHeight: 0
};
const STEP_SCROLL_SX = {
  overflowY: "auto",
  flex: "1 1 auto",
  minHeight: 0,
  paddingLeft: STEP_H_PAD,
  // Same value as the CTA row's paddingRight, which means that on a platform with a classic
  // (space-taking) scrollbar the row's right edge sits ~11px wider than the options while a
  // step overflows, because only this box loses width to the bar. Left as is: macOS/iOS/
  // Android use a 0px overlay bar, and `scrollbarGutter: stable` would reserve the space on
  // those platforms too, which is a worse trade for the common case.
  paddingRight: STEP_PR,
  // Carried over from the body: DtvModal's own ::-webkit-scrollbar rule does not reach this
  // element, but keeping the standard properties keeps the thumb DDS-coloured and the bar in
  // the same overlay mode the body was tuned into.
  scrollbarWidth: "thin",
  scrollbarColor: `${SCROLLBAR_THUMB_COLOR} transparent`
};

// Figma 7190:1404. neutral100 surface inside a neutral700 hairline, and a flex column so the
// CTA row can sit on the bottom edge. minHeight, not height: Figma fixes 500px, but a wrapped
// heading or a tall card must grow rather than clip.
const INPAGE_CARD_SX = {
  // Figma neutral100 maps to DDS neutral.100; the neutral700 hairline maps to gray.300.
  backgroundColor: colorPalette?.neutral?.[100],
  border: `1px solid ${colorPalette?.gray?.[300]}`,
  borderRadius: FusionTheme.fusion.shapes["shape-large"],
  display: "flex",
  flexDirection: "column",
  padding: `36px ${FusionTheme.fusion.space.spacing06}`,
  [INPAGE_TABLET_MQ]: {
    padding: `${FusionTheme.fusion.space.spacing08} ${FusionTheme.fusion.space.spacing08} ${FusionTheme.fusion.space.spacing09}`,
    minHeight: "500px"
  },
  [DESKTOP_MQ]: {
    padding: "32px 52px 40px"
  }
};
// Figma column width for one answer in the two-up grid.
const INPAGE_OPTION_MAX_PX = 390;
// Three answer rows stay fully visible in the card; past that the group scrolls with the
// fourth row peeking, which is the only signal that there are more answers below.
export const INPAGE_MAX_VISIBLE_OPTIONS = 6;
export const INPAGE_PEEK_PX = 28;

// Hoisted so the two variants read as one decision with the difference visible, the way
// STEP_CTA_SX / SHOP_CTA_SX are. (Reference stability is a side benefit, not the point —
// DtvBox is not memoised, and the option handlers below are still fresh closures.)
const STEP_FOOTER_BASE_SX = {
  display: "flex",
  // Stacked (xs) the two CTAs sit flush; the gap is only there to separate them side by
  // side. `0` not `"0px"` so the responsive object still reads as a scale value at xs the
  // way MUI's other numeric spacing keys do. Note this is NOT the whole space between
  // them — the Fusion Button atom carries its own `margin: 10px 8px`, which nothing here
  // overrides, so stacked buttons still clear each other by 20px.
  gap: {
    xs: 0,
    sm: "16px"
  },
  alignItems: {
    xs: "stretch",
    sm: "center"
  },
  flexDirection: {
    xs: "column",
    sm: "row"
  }
};
// In the modal this row sits OUTSIDE the scroll region (see MODAL_BODY_SX_ASK), so it is a
// plain flex item that covers nothing and needs no chrome of its own — no sticky, no opaque
// background, no divider, and no scroll cue (that decision is recorded at STEP_SCROLL_SX).
//
// marginTop 0 with paddingTop 16: the gap above the buttons goes 24px -> 16px, it is not
// removed. The row sits directly against the scroll edge, so a larger gap would read as the
// content having ended there rather than being scrolled.
//
// paddingBottom 16px is the gap under the buttons. It used to live on the modal children
// wrapper, where it was the exact distance the sticky row drifted up at the end of a
// scroll; the wrapper still has no bottom padding, so removing this would shrink the gap
// to 0 rather than restore anything.
//
// flex "0 0 auto" — the row keeps its height and the scroll region absorbs the shortfall,
// bounded by the body's own maxHeight. Measured at 667x375 (landscape phone), the tightest
// case: the region gets 131px, which is 8px LESS than the 139px the old sticky layout left
// uncovered — so a short viewport is squeezed here exactly as much as it was before, and the
// last option is now fully readable instead of sitting under an opaque bar.
const STEP_FOOTER_MODAL_SX = {
  ...STEP_FOOTER_BASE_SX,
  marginTop: 0,
  flex: "0 0 auto",
  paddingLeft: STEP_H_PAD,
  paddingRight: STEP_PR,
  paddingTop: "16px",
  paddingBottom: "16px"
};
// In-page: the card and the step are both flex columns, so `auto` is what rides this row down
// onto the card's bottom edge. A row at every width — Figma sizes the gap between the two
// buttons on phones too, and a gap only reads side by side.
const STEP_FOOTER_INPAGE_SX = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "15px",
  marginTop: "auto",
  paddingTop: "8px",
  // The Fusion atom carries its own horizontal margin, which would add to `gap`. `&&` doubles
  // the class so this wins: the atom sets that margin through `sx` too, and an equal-
  // specificity rule from a parent that renders FIRST loses on source order.
  "&& > *": {
    marginLeft: 0,
    marginRight: 0
  },
  [INPAGE_TABLET_MQ]: {
    gap: "16px"
  }
};

// The step fills the card so the footer above can reach its bottom edge. Same `minHeight: 0`
// argument as STEP_ROOT_MODAL_SX: a column flex item refuses to shrink below its content.
const STEP_ROOT_INPAGE_SX = {
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  minHeight: 0
};

// Header rhythm differs per shell: the modal is tight because its body is already capped,
// the card follows Figma. Hoisted in pairs so the two are one decision, like the footers.
const STEP_PROGRESS_MODAL_SX = {
  display: "block",
  color: colorPalette?.gray?.[500],
  marginBottom: "8px"
};
const STEP_PROGRESS_INPAGE_SX = {
  display: "block",
  // Figma inactive05 maps to DDS gray.500.
  color: colorPalette?.gray?.[500],
  marginBottom: "16px"
};
const STEP_HEADING_MODAL_SX = {
  margin: "0 0 17px",
  outline: "none"
};
const STEP_HEADING_INPAGE_SX = {
  margin: "0 0 16px",
  outline: "none"
};
const STEP_HELPER_MODAL_SX = {
  display: "block",
  color: colorPalette?.jet?.main,
  marginBottom: "11px"
};
const STEP_HELPER_INPAGE_SX = {
  display: "block",
  color: colorPalette?.jet?.main,
  marginBottom: "19px"
};

// The modal keeps one column; the card goes two-up from the tablet width, row-major so DOM
// order stays the reading order the arrow handler walks. `position: relative` is load-
// bearing: it makes this box the offsetParent the cap measures each option against.
const STEP_OPTIONS_MODAL_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "19px",
  maxWidth: "480px"
};
const STEP_OPTIONS_INPAGE_SX = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  rowGap: "14px",
  [INPAGE_TABLET_MQ]: {
    display: "grid",
    gridTemplateColumns: `repeat(2, minmax(0, ${INPAGE_OPTION_MAX_PX}px))`,
    columnGap: "33px",
    rowGap: "19px"
  }
};
// Applied only once the cap has a measured height. Same thumb treatment as the modal region.
const STEP_OPTIONS_SCROLL_SX = {
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: `${SCROLLBAR_THUMB_COLOR} transparent`
};

// Figma writes prices as "$59.99/mo." — the struck pre-promo price uses the whole
// string, the live price is split so dollars render larger than cents/suffix
// (Figma 6976:2364 "Price block": $59 at 24px, .99 and /mo. at 16px).
const fmtPrice = (n: number) => `$${n.toFixed(2)}/mo.`;
const splitPrice = (n: number) => {
  const [dollars, cents] = n.toFixed(2).split(".");
  return {
    dollars: `$${dollars}`,
    cents: `.${cents}`
  };
};
// Price disclosure copy. The figures and the singular/plural branches stay in code —
// the price renders as three differently sized spans (Figma 6976:2364), so a CMS string
// can only own the text AFTER the cents, and asking an author to supply both a singular
// and a plural variant just moves the branch somewhere it cannot be tested.
// Tokens use the house [snake_case] convention (genre-pack-details-panel-adapter.ts:42).
// Deliberately NOT reusing [first_offer_duration]: plan-card fills that from the same
// util but with different text (2-month promo -> "1&2", ours -> "first 2 mos"), and this
// repo already carries two token aliases; a third would be worse than a new name.
const PRICE_COPY_DEFAULTS = {
  // "$64.99/mo. for first 2 mos" — a promo price is only truthful with its window.
  promo: "/mo. for [promo_window]",
  // "+ tax" asserts this price is what the customer keeps paying.
  ongoing: "/mo. + tax",
  // "then $84.99/mo. for mos 2-24"
  nextTier: "then [next_tier_price]/mo. for [next_tier_window]"
};
export type AiRecommenderPriceCopy = Partial<typeof PRICE_COPY_DEFAULTS>;

// A template that lost its token would state a promo price with no duration, or a second
// rate with no amount — the exact misleading claim the disclosure exists to prevent. So a
// missing required token falls back to the built-in wording rather than rendering.
//
// TWO gates, and both are load-bearing — each one alone leaks:
//   - required tokens present: a promo template that simply omits [promo_window] states a
//     promo price with no duration. Nothing survives substitution to catch that, so the
//     template has to be checked BEFORE.
//   - nothing bracketed survives substitution: `ongoing` takes no tokens, so "all required
//     present" is vacuously true there and any string renders verbatim — an author who pasted
//     the promo wording into the ongoing field shipped "$94.99/mo. [promo_window]" to the
//     shopper. Unknown tokens on the other two fields leaked the same way.
const UNFILLED_TOKEN = /\[[a-z0-9_]+\]/i;
const substitute = (text: string, values: Record<string, string>) => Object.keys(values)
  .reduce((out, k) => out.split(`[${k}]`).join(values[k]), text);

const fillTemplate = (template: string | undefined, fallback: string, values: Record<string, string>) => {
  const hasAllTokens = typeof template === "string" && !!template.trim() &&
    Object.keys(values).every(k => template.includes(`[${k}]`));
  const authored = hasAllTokens ? substitute(template as string, values) : null;
  return authored !== null && !UNFILLED_TOKEN.test(authored) ?
    authored :
    substitute(fallback, values);
};

// What follows the cents. THREE states, not two — the middle one is the trap:
//   promo, length known   -> "/mo. for first 2 mos"  (BK 2026-07-29)
//   no promo, established  -> "/mo. + tax"           (this price IS the ongoing rate)
//   promo unknown          -> "/mo."                 (claim nothing)
// The third exists because the upstream caches go silent in the same shape as "no
// promo": when the dtvi benefits array is missing, promoDuration/regularPrice/nextTier
// all fall to null while `price` stays a promo price. Printing "+ tax" there would
// advertise a first-month price as permanent, which is worse than the bare "/mo."
// this component rendered before the disclosure existed. Only the first two are
// authorable — the third is the data-missing fallback, and a blanked field there would
// leave the figure with no unit at all.
const priceSuffix = (
  promoMonths?: number | null,
  hasNextTier?: boolean,
  promoKnown = false,
  copy: AiRecommenderPriceCopy = {}
) => {
  if (promoMonths == null || promoMonths <= 0) {
    return promoKnown ?
      fillTemplate(copy.ongoing, PRICE_COPY_DEFAULTS.ongoing, {}) :
      "/mo.";
  }
  // The token carries the window only — "for" belongs to the template, so an author can
  // reword the connector without the branch here having to know about it.
  const promoWindow = promoMonths === 1 ? "1st mo" : `first ${promoMonths} mos`;
  const resolved = fillTemplate(copy.promo, PRICE_COPY_DEFAULTS.promo, { promo_window: promoWindow });
  // The comma hands off to the "then $X…" line below (BK 2026-07-29). Skipped when the
  // author already ended the sentence with one, so the handoff never doubles up.
  return hasNextTier && !resolved.trim().endsWith(",") ? `${resolved},` : resolved;
};

// Second disclosure line, for signatures whose live price only holds one month
// (BK 2026-07-29): "then $84.99/mo. for mos 2-24". Month wording reuses the house
// util plan-card/flip-cards already fill `[first_offer_duration]` with, so the two
// surfaces phrase the same window identically ((2,24) -> "2-24", (2,3) -> "2&3",
// (2,2) -> "2nd mo" — which already reads as a single month, hence no "mos").
// A "then $X" line is only ever the continuation of a first window, so it answers to
// the same condition the suffix does. Without that gate a rejected window renders the
// self-contradicting pair "$94.99/mo. + tax" / "then $84.99/mo. for mos 2-24".
// adaptCatalog already drops nextTier when the window is unusable, but this component also
// renders from hand-built catalogs (sandbox mock, harnesses, configurator) that JSON
// round-trip their fixtures, and nothing above it is an error boundary — a string price would
// throw on .toFixed and take the whole section down. So the figures are re-checked here
// instead of trusting the shape.
const nextTierLine = (
  tier?: ResolvedPriceTier | null,
  promoMonths?: number | null,
  copy: AiRecommenderPriceCopy = {}
) => {
  if (!tier || promoMonths == null || promoMonths <= 0) {
    return null;
  }
  if (!Number.isFinite(tier.price)) {
    return null;
  }
  // adaptCatalog's window invariant, re-checked for the same hand-built catalogs as the
  // price check above: integer months, continues the first window (startMo = promoMonths
  // + 1), not reversed. A window that skips or reorders months is wrong price copy —
  // worse than omitting the line. Integer checks subsume the old isFinite ones.
  if (!Number.isInteger(tier.startMo) || !Number.isInteger(tier.endMo) ||
    tier.startMo !== promoMonths + 1 || tier.endMo < tier.startMo) {
    return null;
  }
  const span = getMultiOfferDuration(tier.startMo, tier.endMo);
  return fillTemplate(copy.nextTier, PRICE_COPY_DEFAULTS.nextTier, {
    // fmtPrice already appends "/mo."; the template supplies its own, so pass the figure.
    next_tier_price: `$${tier.price.toFixed(2)}`,
    next_tier_window: tier.startMo === tier.endMo ? span : `mos ${span}`
  });
};

// The display-only figures the engine never reads. adaptCatalog returns
// PricedCatalog, so these come off a typed field rather than a cast.
//
// `channels` is the authored count line ("125+ channels") that catalog-adapter joins
// off packageConfig's channel block. It is null for any product with no authored
// block, and the card drops the line rather than printing a bare number.
//
// promoKnown is opt-in, not opt-out. The whole point of the flag is that "+ tax" asserts the
// price is permanent, so the absence of information has to produce the weaker claim, not the
// stronger one. Defaulting to true meant a catalog built by hand — the sandbox mock, a test
// harness, the configurator, or a failed signatures lookup that lands here as null — asserted
// permanence it had no basis for. adaptCatalog always sets a real boolean, so the production
// path is unchanged by this.
const cardMetaOf = (pack?: PricedCatalogPackage | null) => ({
  promoMonths: pack?.promoDuration ?? null,
  nextTier: pack?.nextTier ?? null,
  promoKnown: pack?.promoKnown === true,
  channels: pack?.channels ?? null
});

const storageKey = (id?: string) => `uf-ai-recommender:${id || "default"}`;
// Element ids are scoped by BLOK id as well as question id. Question ids come from the
// recipe, so two recommender bloks sharing a recipe on one page would mint the same ids
// three times over and every aria-labelledby / aria-describedby / focus lookup would
// resolve to the first instance. The session key is already per-blok for the same reason.
//
// The scope is NOT `id || "default"` the way storageKey is. The adapter can hand back
// `id: undefined` (`content?.id || content?._uid || undefined`), and a shared literal would
// put the collision straight back. The caller substitutes a useId() instead — the session
// key still wants the stable literal, because a per-mount scope would lose the saved run.
//
// That split leaves one known gap: two bloks that BOTH lack an id get separate element ids
// but still share `uf-ai-recommender:default`, so a refresh drops the second one into the
// first one's answers. Not fixed here because the trade runs the other way (a per-mount
// storage key would break resume for every single-blok page, which is all of them), and
// because real authored content carries an id — checked against the live story, where the
// top-level blok has `id: "ai-product-recommender-1"`.
const domId = (blokId: string, kind: string, questionId: string) => `apr-${blokId}-${kind}-${questionId}`;

// Deliberately NOT whether the modal was open (BK 2026-07-30). An overlay that reappears
// on refresh takes over the page with no user gesture, and this modal sits outside the
// app's modalPriorityMap / session-fatigue orchestration, so it could stack with Exit V2 /
// Revisitor / Idle Timer. This build only ever WRITES a completed run, so `phase` is
// "results" on every row it creates — but a row left by an OLDER build can still say "ask",
// which is why the restore effect tests the field and drops anything that is not "results".
// Do not delete that read-side check on the strength of the write-side rule.
interface StoredSession {
  answers?: Answers;
  /** "results" on every row this build writes; an "ask" row means an older build wrote it. */
  phase?: "ask" | "results";
}

const readSession = (key: string): StoredSession | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
};
const writeSession = (key: string, value: StoredSession) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (_e) {
    // storage unavailable (private mode / quota) — resume is best-effort
  }
};
const clearSession = (key: string) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.removeItem(key);
  } catch (_e) {
    // ignore
  }
};

type SecondaryAction = { label: string; onClick: () => void };

// Mount-gated matchMedia: false on SSR and on the first client render, so hydration always
// agrees. Only the in-page card needs a JS boolean — the answers cap is measured in pixels,
// and the CTA font rides a Fusion prop that no media query can reach.
const useMinWidth = (query: string, enabled: boolean): boolean => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (!enabled || typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mql = window.matchMedia(query);
    const update = () => setMatches(mql.matches);
    update();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", update);
      return () => mql.removeEventListener("change", update);
    }
    // Safari < 14 has only the deprecated pair.
    mql.addListener(update);
    return () => mql.removeListener(update);
  }, [query, enabled]);
  return matches;
};

type QuestionStepProps = {
  /** id scope — the blok id, or a useId() when the CMS gave none; keeps two bloks apart */
  blokId: string;
  question: RecipeQuestion;
  selected: string[];
  progressText: string;
  selectHelper: string;
  isLast: boolean;
  nextLabel: string;
  resultsCtaLabel: string;
  secondary?: SecondaryAction;
  /** AC5/AC6 — one position for THIS question, one name per option: the DDS helper names the
      click off aria-label, so a shared pair would report every option under one name. */
  optionLinkAttrs: (linkName: string) => Partial<LinkClickAttrs>;
  /** AC7 — shared by Next/Back/Close, which the ticket gives one position without the progress token */
  navLinkAttrs: (linkName: string) => Partial<LinkClickAttrs>;
  /** modal mode splits the step into a scroll region + a CTA row below it; in-page keeps one flow */
  inModal?: boolean;
  /** in-page only: the two-column tiers, where the answers group can overflow the card */
  inpageWide?: boolean;
  onToggle: (optId: string) => void;
  onNext: () => void;
};

const QuestionStep = ({
  blokId,
  question,
  selected,
  progressText,
  selectHelper,
  isLast,
  nextLabel,
  resultsCtaLabel,
  secondary,
  optionLinkAttrs,
  navLinkAttrs,
  inModal,
  inpageWide,
  onToggle,
  onNext
}: QuestionStepProps) => {
  const single = question.mode === "single";
  // Count only picks the CURRENT recipe still offers: `selected` is keyed by question id and
  // can hold an id no option has (the same state the activeIndex clamp below exists for), and
  // a stale id must not satisfy the gate while every option renders unchecked.
  const pickedCount = question.options.filter(opt => selected.includes(opt.id)).length;
  const canNext = single ? pickedCount === 1 : pickedCount > 0;
  const advanceLabel = isLast ? resultsCtaLabel : nextLabel;
  const headingId = domId(blokId, "q", question.id);
  const helperId = domId(blokId, "help", question.id);
  const labelId = domId(blokId, "label", question.id);
  const optionsId = domId(blokId, "opts", question.id);
  const [capPx, setCapPx] = useState<number | null>(null);
  const optionCount = question.options.length;
  const capped = !inModal && !!inpageWide && optionCount > INPAGE_MAX_VISIBLE_OPTIONS;

  // Past three rows the card would grow past the height Figma fixes, so the group scrolls
  // instead — capped just below the first hidden answer so that row peeks. A layout effect
  // because the height comes from offsetTop, which no media query can read.
  useLayoutEffect(() => {
    if (!capped) {
      setCapPx(null);
      return undefined;
    }
    const group = document.getElementById(optionsId);
    if (!group) {
      return undefined;
    }
    const measure = () => {
      const firstHidden = group.children[INPAGE_MAX_VISIBLE_OPTIONS] as HTMLElement | undefined;
      setCapPx(firstHidden ? firstHidden.offsetTop + INPAGE_PEEK_PX : null);
    };
    measure();
    // The group's own width is what changes when the viewport re-flows the rows. jsdom has
    // no ResizeObserver, so there the cap stays whatever the first measure found.
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(group);
    return () => observer.disconnect();
  }, [capped, optionsId, optionCount]);

  const inpageOptionsSx = capPx === null ? STEP_OPTIONS_INPAGE_SX : {
    ...STEP_OPTIONS_INPAGE_SX,
    ...STEP_OPTIONS_SCROLL_SX,
    maxHeight: toPx(capPx)
  };
  const optionsSx = inModal ? STEP_OPTIONS_MODAL_SX : inpageOptionsSx;
  // The Fusion atom spreads its own size typography AFTER `sx`, so the smaller phone label
  // can only come from the `size` prop.
  const ctaSize = inModal || inpageWide ? "medium" : "small";
  // The single tab stop. Derived rather than tested per option, because
  // `isSelected || (nothing selected && index === 0)` leaves the group with NO tab stop
  // whenever `selected` holds an id that no current option has — a state nothing wires
  // today, but one this component must stay safe in (pinned by the spec's "keeps a tab
  // stop when the selected answer names an option that no longer exists"). The group would
  // then be unreachable by Tab, and unreachable by arrow too (arrows need focus inside it)
  // — strictly worse than the bug this replaced. Clamping to 0 makes that state
  // unrepresentable instead of guarding against it.
  const activeIndex = Math.max(0, question.options.findIndex(opt => selected.includes(opt.id)));

  // ARIA radiogroup keyboard support. Measured missing on dev1 2026-08-09: every option
  // carried tabIndex 0 (no roving tabindex) and a real ArrowDown moved neither focus nor
  // selection, so the markup promised radio semantics the keyboard did not deliver.
  //
  // Arrows both move focus AND select, which is the radio pattern — so this deliberately
  // selects by clicking the option, which is what makes GTM report the same AC5/AC6
  // link_click a mouse click reports.
  // Known trade-off, not an equivalence: a shopper who arrows from option 1 to option 4
  // reports THREE selections (2, 3, 4) where a mouse shopper clicking option 4 reports one.
  // Every option arrowed past really was selected on the way, so the events are truthful,
  // but keyboard sessions will read heavier in AC5 than mouse sessions. The alternative the
  // APG allows — arrows move focus, Space/Enter commits — would report one, at the cost of
  // an extra keystroke; that is a product call, not a code call.
  // Single-select only; a checkbox group is navigated with Tab, not arrows.
  //
  // Arrows only — no Home/End. The APG radio pattern does not define them, and inside the
  // modal's scroll region Home is a plausible "scroll back to the question" press, which
  // this handler would have swallowed and turned into a silent answer change.
  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLElement>, index: number) => {
    const { key } = event;
    if (!["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft"].includes(key)) {
      return;
    }
    // `key` does not carry modifiers, so without this Alt+Left (browser back), Cmd+Down
    // (macOS end-of-document, i.e. the Home/End this handler deliberately does not claim)
    // and VoiceOver's Ctrl+Opt+arrows all land here — cancelled, answer silently changed,
    // and a false AC5 reported.
    if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
      return;
    }
    // Holding the key is one gesture, not N deliberate choices, and every selection here
    // reports an AC5 link_click — so an unguarded auto-repeat inflates that metric by
    // however many times the OS repeats. The default is deliberately left alone for the
    // repeat, so a held arrow scrolls the region instead of doing nothing at all.
    if (event.repeat) {
      return;
    }
    const count = question.options.length;
    const nextIndex = key === "ArrowDown" || key === "ArrowRight" ?
      (index + 1) % count :
      (index - 1 + count) % count;
    // `closest`, not `parentElement`: any future wrapper around an option (a tooltip, an
    // <li>, a DDS swap) would silently make parentElement the wrapper, so the lookup would
    // return one node and the move would no-op — while still changing the answer below.
    const group = (event.currentTarget as HTMLElement).closest('[role="radiogroup"]');
    const target = group?.querySelectorAll('[role="radio"]')[nextIndex] as HTMLElement | undefined;
    // A DOM shape this handler does not understand. Fall through to the browser rather than
    // swallowing the key: with roving tabindex the other options are not Tab-reachable, so a
    // silent no-op here would strand them.
    if (!target) {
      return;
    }
    // The option's own id, read back off the node that is about to take focus — so focus and
    // selection cannot disagree if the DOM order ever stops matching the options array.
    const nextId = target.dataset.optionId;
    if (!nextId) {
      return;
    }
    // Only now is the move certain, so this is the first point where cancelling the default
    // (which would otherwise scroll the region out from under the focused option) is correct.
    event.preventDefault();
    target.focus();
    // Re-reporting a selection the shopper already holds is a duplicate AC5, not a choice.
    // Reachable on a one-option question, where every arrow resolves back to index 0.
    //
    // The option's own click, not onToggle directly. GTM reports a link_click off a click
    // event, so calling onToggle here would change the answer and report nothing — which is
    // the AC5/AC6 event keyboard users got from the push this replaced. The option's onClick
    // is onToggle(opt.id) and nothing else, so the answer change is unaffected.
    if (!selected.includes(nextId)) {
      target.click();
    }
  };

  return (
    <DtvBox sx={inModal ? STEP_ROOT_MODAL_SX : STEP_ROOT_INPAGE_SX}>
      {/* In the modal this is the scroll region; in the card it is an inert wrapper — there
          the only thing that scrolls is the answers group, and it caps itself. */}
      <DtvBox sx={inModal ? STEP_SCROLL_SX : undefined}>
        {/* aria-hidden because the same string is now part of the heading's accessible name
            below, and the heading is what focus lands on when a step changes. Leaving both
            exposed reads "2 of 3" twice to anyone browsing linearly. */}
        <DtvText
          aria-hidden="true"
          content={progressText}
          variant="textlink3medium"
          sx={inModal ? STEP_PROGRESS_MODAL_SX : STEP_PROGRESS_INPAGE_SX}
        />
        <DtvBox
          component="h3"
          id={headingId}
          tabIndex={-1}
          sx={inModal ? STEP_HEADING_MODAL_SX : STEP_HEADING_INPAGE_SX}
        >
          {/* Advancing a step already moves focus here (see the effect that focuses
              headingId), so the question label IS announced — measured on dev1. What was
              missing is WHERE the shopper is: focus read "What do you watch most?" with no
              "2 of 3". Folding the progress into this heading's accessible name fixes the
              announcement without a live region, which would have double-announced against
              that same focus move.
              The group below deliberately does NOT point here: several screen readers
              re-read a group's name on entry and some repeat it per option, and arrows are
              the primary navigation in this group — so labelling it with the heading would
              re-read "2 of 3" on every press. It points at the question label alone. */}
          <DtvBox
            component="span"
            sx={SR_ONLY_SX}
          >
            {`${progressText}. `}
          </DtvBox>
          {/* Figma 6976:2262 title = 36px navy. subhead1medium only lands on 36px in
              the 1200-1439px band — it keeps climbing above that (measured live:
              40px at 1500, 44px at 1750) — so the two upper tiers get pinned the
              same way the results heading does. Tablet/mobile stay on the DDS
              ladder (32/24), which already matches the comp. */}
          <DtvText
            id={labelId}
            content={question.label}
            variant="subhead1medium"
            sx={{
              color: colorPalette?.jet?.main,
              ...PIN_36
            }}
          />
        </DtvBox>
        {selectHelper && (
          <DtvText
            id={helperId}
            content={selectHelper}
            variant="textlink3medium"
            sx={inModal ? STEP_HELPER_MODAL_SX : STEP_HELPER_INPAGE_SX}
          />
        )}
        {/* aria-describedby: "Select one" / "Select all that apply" is the only thing that
            tells a shopper the two modes apart, and it was visual-only — the group announced
            its label and nothing about how many options to pick.
            The helper deliberately stays VISIBLE and un-hidden, unlike the progress line
            right above it. Progress had to be aria-hidden because it was folded into the
            heading's own accessible name, so leaving it exposed read "2 of 2" twice in one
            announcement. A describedby target is a separate, later utterance and is the
            documented APG pattern — hiding it would leave sighted-plus-SR users with copy
            they can see and never hear. Do not "make these consistent". */}
        <DtvBox
          id={inModal ? undefined : optionsId}
          role={single ? "radiogroup" : "group"}
          aria-labelledby={labelId}
          aria-describedby={selectHelper ? helperId : undefined}
          sx={optionsSx}
        >
          {question.options.map((opt, index) => {
            const isSel = selected.includes(opt.id);
            // Roving tabindex: a radiogroup is ONE tab stop, entered on the checked option
            // (or the first, before anything is checked) and traversed with arrows. Every
            // option being tabIndex 0 is what made the arrow-key gap invisible in review —
            // the options were all reachable, just not the way `role="radio"` promises.
            // Checkbox groups keep every option tabbable, which is correct for them.
            const optionTabIndex = single ? (index === activeIndex ? 0 : -1) : 0;
            return (
              <DtvBox
                key={opt.id}
                component="button"
                role={single ? "radio" : "checkbox"}
                aria-checked={isSel}
                // Read back by the arrow handler off the node it is about to focus, so the
                // option that takes focus and the option that gets selected are the same
                // one by construction rather than by the two orders happening to agree.
                data-option-id={opt.id}
                {...optionLinkAttrs(opt.label)}
                tabIndex={optionTabIndex}
                onKeyDown={single ? (event: React.KeyboardEvent<HTMLElement>) => handleOptionKeyDown(event, index) : undefined}
                onClick={() => onToggle(opt.id)}
                sx={{
                  textAlign: "left",
                  padding: "16px 30px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: isSel ? colorPalette?.jet?.main : "transparent",
                  color: isSel ? colorPalette?.white?.main : colorPalette?.black?.main,
                  border: `1px solid ${isSel ? colorPalette?.jet?.main : colorPalette?.neutral?.[300]}`,
                  "&:focus-visible": { outline: `2px solid ${colorPalette?.blue?.[600]}` }
                }}
              >
                <DtvText content={opt.label} variant="smalltextmedium" />
              </DtvBox>
            );
          })}
        </DtvBox>
      </DtvBox>
      <DtvBox
        sx={inModal ? STEP_FOOTER_MODAL_SX : STEP_FOOTER_INPAGE_SX}
      >
        {secondary && (
          <Button
            buttontype="secondary"
            mode="light"
            isTangerine
            size={ctaSize}
            {...navLinkAttrs(secondary.label)}
            onClick={secondary.onClick}
            sx={inModal ? STEP_CTA_SX : STEP_CTA_INPAGE_SX}
          >
            {secondary.label}
          </Button>
        )}
        <Button
          buttontype="primary"
          mode="light"
          isTangerine
          size={ctaSize}
          disabled={!canNext}
          {...navLinkAttrs(advanceLabel)}
          onClick={onNext}
          sx={inModal ? STEP_CTA_SX : STEP_CTA_INPAGE_SX}
        >
          {advanceLabel}
        </Button>
      </DtvBox>
    </DtvBox>
  );
};

type ResultCardProps = {
  title: string;
  price: number | null;
  regularPrice?: number | null;
  /** Months the live price holds; null means it is the ongoing rate. */
  promoMonths?: number | null;
  /** What the price becomes after `promoMonths`, when that is known. */
  nextTier?: ResolvedPriceTier | null;
  /** false = the source could not establish whether `price` is a promo. */
  promoKnown?: boolean;
  /** Authored channel-count line ("125+ channels"); omitted when the CMS has none. */
  channels?: string | null;
  /** CMS disclosure templates; same for every card, so threaded as one object. */
  priceCopy?: AiRecommenderPriceCopy;
  ctaUrl: string | null;
  ctaLabel: string;
  tagLabel?: string;
  legal?: string;
  /** In-page only: a second CTA beside "Shop now". Undefined renders nothing. */
  secondaryCta?: { label: string; url: string; linkAttrs: Partial<LinkClickAttrs> };
  /** AC11 — the shop CTA's pair. AC12's lives on secondaryCta, which has its own destination. */
  ctaLinkAttrs: Partial<LinkClickAttrs>;
  onCtaClick: () => void;
};

// Card chrome per Figma 6976:2341 (452x343): white surface, 12px radius, elevation
// drop shadow, NO border. Content insets are 20 left/right, 32 top (wrapper 9 +
// header 23) and 28 bottom.
const CARD_SX = {
  // `white.main` is #FEFEFE; Figma's surface-bg-02 is pure white.
  backgroundColor: colorPalette?.neutral_white?.main,
  borderRadius: "12px",
  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.16)",
  padding: "32px 20px 28px",
  flex: "1 1 280px",
  maxWidth: "452px",
  // A column so the CTA box can sit on the bottom edge: side-by-side cards stretch to the same
  // height, and their buttons must line up whatever each card's price block needs (BK 2026-09-03).
  display: "flex",
  flexDirection: "column"
};
// Figma 7190:1404 spreads the pair inside the in-page card; stacked phones keep the tight gap.
const RESULTS_ROW_MODAL_SX = {
  display: "flex",
  flexWrap: "wrap",
  gap: "16px",
  alignItems: "stretch"
};
const RESULTS_ROW_INPAGE_SX = {
  ...RESULTS_ROW_MODAL_SX,
  [INPAGE_TABLET_MQ]: {
    gap: "51px"
  }
};
// One CTA needs no row; two do. From the tablet width up the pair stays on one line whatever
// the card width (BK 2026-09-03); below it the row may wrap, because a 253px phone row cannot
// hold both labels at their content widths.
const CARD_CTA_SX = {
  marginTop: "auto",
  paddingTop: "6px"
};
const CARD_CTA_ROW_SX = {
  ...CARD_CTA_SX,
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "11px",
  // Same reason as STEP_FOOTER_INPAGE_SX: the atom's own horizontal margin would add to it.
  "&& > *": {
    marginLeft: 0,
    marginRight: 0
  },
  [INPAGE_TABLET_MQ]: {
    flexWrap: "nowrap",
    gap: "24px"
  }
};

// Figma's "Desktop" tier is 36px (title 24px), but DDS keeps scaling past 1440px
// (subhead1medium 40px at 1440-1699 and 44px at 1700+; subhead2medium 28px at
// 1700+). Pin the two upper tiers so every desktop width renders the Figma size —
// the tablet/mobile steps already agree, so they stay on the DDS ladder.
const pinFontSize = (px: string) => ({
  [MEDIAQUERY_DESKTOP]: { fontSize: px },
  [MEDIAQUERY_LARGE_DESKTOP]: { fontSize: px }
});

// Every heading this component owns settles on 36px, so build the pin once rather
// than calling pinFontSize on each render — the same reason CARD_SX / CHIP_LABEL_SX /
// LEGAL_SX live at module scope. Two of the four call sites still spread it into a
// literal because they add a colour; the other two pass it straight through.
const PIN_36 = pinFontSize("36px");

// 12px Roboto Medium (chip) and 11px Roboto Regular (legal) have no DDS typography
// variant, and a DtvText variant always wins on font-size: the variant's @media
// rules and an `sx` fontSize land in the SAME emotion class with the media blocks
// last. So anything off-variant is styled on a plain DtvBox instead.
const CHIP_LABEL_SX = {
  fontFamily: "roboto-medium",
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: 1.3,
  // Roboto styles carry no tracking in Figma; without this the box inherits the
  // page's negative letter-spacing.
  letterSpacing: 0,
  // Figma --yellow900; DDS colorPalette.yellow only goes 200/300/400/600.
  color: "#2B250C",
  whiteSpace: "nowrap"
};

const LEGAL_SX = {
  display: "block",
  fontFamily: "roboto-regular",
  fontSize: "11px",
  fontWeight: 400,
  lineHeight: 1.3,
  letterSpacing: 0,
  // Figma paints this pure black; #121212 is the closest DDS token.
  color: colorPalette?.black?.main,
  // Figma 6976:2367 caps the legal copy at 315px so it wraps before the card edge.
  maxWidth: "315px",
  margin: 0
};

// Figma 6976:2364: $59 at 24px/1.2, .99 and /mo. at 16px/1.3 (the suffix in the
// regular cut), all text-primary, baseline-aligned with a 2px gap.
const PRICE_DOLLARS_SX = {
  fontFamily: "PFDinMed",
  fontSize: "24px",
  fontWeight: 500,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: colorPalette?.jet?.main
};
const PRICE_CENTS_SX = {
  ...PRICE_DOLLARS_SX,
  fontSize: "16px",
  lineHeight: 1.3
};
const PRICE_SUFFIX_SX = {
  ...PRICE_CENTS_SX,
  fontFamily: "PFDinReg",
  fontWeight: 400
};
// The "then $X…" line is price information, not legal fine print, so it keeps the
// price block's face and only steps down in size. Figma has no node for it — it is
// the two-tier disclosure BK asked for on 2026-07-29.
const NEXT_TIER_SX = {
  ...PRICE_SUFFIX_SX,
  display: "block",
  fontSize: "14px"
};
// Visually hidden but readable by assistive tech. Same recipe as
// flip-cards/components/FlipCard/PricingSection.tsx:74-85 (clipPath + margin:-1px
// keeps the node out of layout flow, unlike display:none which removes it from the
// a11y tree too).
const SR_ONLY_SX = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
  border: 0
};
// Figma button widths. Hoisted for the same reason CARD_SX is: an inline literal is a
// fresh object every render. `minWidth` (not width) keeps the Fusion default hug so a
// longer label grows instead of wrapping.
const SHOP_CTA_SX = {
  minWidth: "196px",
  whiteSpace: "nowrap"
};
const STEP_CTA_SX = {
  minWidth: "150px",
  whiteSpace: "nowrap"
};
// Figma 7190:1405 narrows the in-page pair on phones; the height comes with `size="small"`.
const STEP_CTA_INPAGE_SX = {
  minWidth: "111px",
  whiteSpace: "nowrap",
  [INPAGE_TABLET_MQ]: {
    minWidth: "150px"
  }
};
const START_OVER_SX = {
  minWidth: "118px",
  whiteSpace: "nowrap"
};
const DEFAULT_SECONDARY_CTA_LABEL = "See all packages";
// Two CTAs share the row equally when it is wide enough: basis 0 + grow 1 makes each
// (row - gap) / 2. The atom's content-based minimum stops the longer label from being clipped
// when the row is not that wide, so the pair goes unequal before it goes unreadable.
const CARD_CTA_IN_ROW_SX = {
  flex: "1 1 0",
  whiteSpace: "nowrap"
};
const ENTRY_CTA_SX = {
  minWidth: "244px",
  whiteSpace: "nowrap"
};
// Figma 7190:1120 narrows the in-page entry CTA on phones.
const ENTRY_CTA_INPAGE_SX = {
  minWidth: "163px",
  whiteSpace: "nowrap",
  [DESKTOP_MQ]: {
    minWidth: "244px"
  }
};
// The atom's own margin would indent the CTA off the copy's left edge and pad the gap above it;
// doubled `&` because the atom sets that margin through its own sx (same specificity).
const ENTRY_CTA_WRAP_INPAGE_SX = {
  "&& > *": {
    margin: 0
  }
};

// Figma's Start over button carries a 16px "skip_back" left icon (a circular
// restart arrow). Fusion's icon set has no equivalent — curvedArrowLeft is a plain
// left arrow and back15Sec bakes "15" into the glyph — so the vector is inlined
// here and drawn in currentColor. Replace this with the Fusion atom once one ships.
//
// Fusion's Button renders `leftIcon` INSIDE its own <svg viewBox="0 0 36 36"> sized
// to 16px, so this has to be SVG children in 36-unit space, not a wrapped element.
// Figma insets the 12.585x12.7493 glyph by 11.94%/10.16% of the 16px slot, which at
// 36/16 = 2.25 units per px is a translate(4.3, 3.66) after scaling.
const StartOverIcon = () => (
  <g transform="translate(4.3 3.66) scale(2.25)">
    <path
      d="M0.223036 2.37909L0.955024 2.76664C1.60746 1.81632 2.50541 1.0607 3.55322 0.580204C4.60103 0.0997099 5.75947 -0.0876743 6.90533 0.0380018C8.05118 0.163678 9.14152 0.59771 10.0603 1.2939C10.979 1.99008 11.6918 2.92236 12.1228 3.99151C12.5537 5.06065 12.6867 6.22664 12.5075 7.36537C12.3284 8.50409 11.8439 9.57292 11.1055 10.4581C10.3671 11.3433 9.40243 12.0117 8.31429 12.3921C7.22616 12.7726 6.05526 12.8509 4.92616 12.6186C3.86936 12.4039 2.88468 11.9236 2.06494 11.2229C1.2452 10.5222 0.617426 9.62425 0.240831 8.61375C0.137275 8.33864 0.0288084 7.92397 0.0288084 7.92397C0.0233982 7.90377 0.0221412 7.88267 0.0251191 7.86196C0.028097 7.84126 0.035284 7.82136 0.0461695 7.8035C0.057055 7.78563 0.0714502 7.77016 0.0884872 7.75802C0.105524 7.74588 0.124796 7.73733 0.145236 7.73287L1.13992 7.51064C1.18202 7.50185 1.22591 7.50941 1.26264 7.53178C1.29937 7.55415 1.32622 7.58965 1.33773 7.63109C1.33773 7.63109 1.41459 7.92531 1.49636 8.1422C1.88321 9.17341 2.59768 10.0493 3.53011 10.6355C4.46254 11.2217 5.56148 11.4859 6.65847 11.3875C7.75547 11.2892 8.78995 10.8337 9.60324 10.091C10.4165 9.34832 10.9637 8.35935 11.161 7.27577C11.3582 6.19218 11.1946 5.07381 10.6953 4.09214C10.1959 3.11047 9.38828 2.31969 8.39632 1.84108C7.40436 1.36246 6.28281 1.22241 5.20361 1.44242C4.12442 1.66243 3.14715 2.23035 2.42171 3.05909C2.31327 3.18309 2.21323 3.31331 2.11768 3.44664L2.80659 3.85953C2.94392 3.93997 2.93727 4.0582 2.79194 4.12264L2.34749 4.31953L0.51633 5.09242C0.493932 5.10494 0.468748 5.11141 0.443088 5.11118C0.417428 5.11094 0.392231 5.10402 0.370063 5.0911C0.347894 5.07817 0.329521 5.0597 0.316677 5.03748C0.303834 5.01526 0.296959 4.99008 0.296821 4.96442L0.289659 4.89198L0.204373 4.01686L0.0487736 3.0062L0.00168163 2.5222C-0.0143184 2.36264 0.0857025 2.29864 0.223036 2.37909Z"
      fill="currentColor"
    />
  </g>
);

const ResultCard = ({
  title,
  price,
  regularPrice,
  promoMonths,
  nextTier,
  promoKnown,
  channels,
  priceCopy,
  ctaUrl,
  ctaLabel,
  tagLabel,
  legal,
  secondaryCta,
  ctaLinkAttrs,
  onCtaClick
}: ResultCardProps) => {
  // Derived once: the three price parts and the two disclosure strings were each
  // recomputed several times per render inside the JSX below.
  // Number.isFinite, not `!= null`: `price` is typed number|null but arrives from CMS/epoch
  // JSON, and a hand-authored or partially-migrated feed can carry "64.99" as a string.
  // `.toFixed` on a string throws, and there is no error boundary above this component
  // (the only one in the tree is SandboxMainCanvas), so the whole page would blank.
  // The render gate below MUST test `parts`, not `price` — `parts.dollars` is dereferenced
  // unconditionally inside it, so gating on `price != null` there would turn the .toFixed
  // throw into a null-deref throw instead of skipping the block.
  const parts = Number.isFinite(price) ? splitPrice(price as number) : null;
  // Order matters: the suffix's trailing comma hands off to a line that must exist, so
  // it is driven by the rendered line itself, not by nextTier being merely present.
  const secondLine = nextTierLine(nextTier, promoMonths, priceCopy);
  // "+ tax" says the shown price is permanent. A struck price says the opposite, so a
  // strikethrough with no stated window withholds the claim exactly as promoKnown false
  // does — otherwise one card asserts the price is both promotional and permanent.
  // `"64.99" > 49.99` is true in JS, so a `!= null` gate here would let a string regularPrice
  // reach fmtPrice's .toFixed further down and throw.
  const strikesPrice = Number.isFinite(regularPrice) && Number.isFinite(price) &&
    (regularPrice as number) > (price as number);
  const hasWindow = promoMonths != null && promoMonths > 0;
  const suffix = priceSuffix(promoMonths, !!secondLine, promoKnown && !(strikesPrice && !hasWindow), priceCopy);
  return (
    <DtvBox sx={CARD_SX}>
    {/* Figma 6976:2346 keeps a 4px gap under the title for the package description.
        The channel count below now fills that slot when the CMS authors one; when it
        does not, the gap alone is what is left of the row. */}
    <DtvBox
      component="h4"
      sx={{ margin: "0 0 4px" }}
    >
      <DtvText
        content={title}
        variant="subhead2medium"
        sx={{
          color: colorPalette?.jet?.main,
          // The variant ships 1.2em; Figma's Signature Titles block is 1.3.
          lineHeight: 1.3,
          ...pinFontSize("24px")
        }}
      />
    </DtvBox>
    {/* Authored channel count, in the row Figma 6976:2346 reserves under the title.
        Plain text, not a link: the comp underlines it, but nothing in the recommender
        payload carries a channel-lineup URL, and an underline with no destination reads
        as a broken link. Rendered on a DtvBox rather than a DtvText variant because
        14px Roboto Regular has no DDS typography variant and a variant's @media
        font-size always beats an sx one (see the note above pinFontSize). */}
    {channels && (
      <DtvBox
        sx={{
          margin: "0 0 4px",
          // The theme registers Roboto under hyphenated @font-face families
          // (src/theme/dtvTheme.tsx:389-407); a bare "Roboto" is not one of them and
          // would fall through to the generic sans-serif wherever the OS lacks it.
          fontFamily: "roboto-regular",
          fontSize: "14px",
          lineHeight: "20px",
          // Same reason as CHIP_LABEL_SX/LEGAL_SX: Roboto carries no tracking in
          // Figma, and without this the box inherits the page's negative value.
          letterSpacing: 0,
          color: colorPalette?.gray?.[300]
        }}
      >
        {channels}
      </DtvBox>
    )}
    {/* Figma 6976:2360/2361: the offer tag sits between the title block and the
        price, with 6px above it and 4px below. */}
    {tagLabel && (
      <DtvBox
        sx={{
          paddingTop: "6px",
          paddingBottom: "4px"
        }}
      >
        <DtvBox
          sx={{
            display: "inline-flex",
            alignItems: "center",
            backgroundColor: colorPalette?.yellow?.[200],
            borderRadius: "2px",
            padding: "4px 8px"
          }}
        >
          <DtvBox
            component="span"
            sx={CHIP_LABEL_SX}
          >
            {tagLabel}
          </DtvBox>
        </DtvBox>
      </DtvBox>
    )}
    {parts && (
      <DtvBox>
        {/* Figma stacks the struck price ABOVE the live one and draws the line in
            red (Fusion Strikethrough) while the text stays text-secondary. */}
        {/* aria-hidden because the strikethrough is only meaningful next to the live
            price, and a screen reader reading two bare figures in sequence cannot tell
            which is which. The SR-only string below names both in one sentence.
            DtvText spreads ...otherProps, so this reaches the element. */}
        {strikesPrice && (
          <DtvText
            content={fmtPrice(regularPrice)}
            variant="body3"
            aria-hidden="true"
            sx={{
              // inline-block so the rule only spans the figure, as in Figma.
              display: "inline-block",
              textDecoration: "line-through",
              textDecorationColor: colorPalette?.error1?.main,
              color: colorPalette?.gray?.[300]
            }}
          />
        )}
        <DtvBox
          component="span"
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: "2px"
          }}
        >
          {/* The figure is split into three sized spans for Figma, which leaves no
              single node a screen reader can read as one price. An aria-label on this
              wrapper would be ignored: a bare span is role=generic, which ARIA
              prohibits naming (axe aria-prohibited-attr). Same fix flip-cards uses —
              real SR-only text plus aria-hidden on the visual parts
              (flip-cards/components/FlipCard/PricingSection.tsx:71-92). */}
          <DtvBox component="span" sx={SR_ONLY_SX}>
            {strikesPrice ?
              `Regular price ${fmtPrice(regularPrice)}, now monthly price ${parts.dollars}${parts.cents}${suffix}` :
              `Monthly price ${parts.dollars}${parts.cents}${suffix}`}
          </DtvBox>
          <DtvBox
            component="span"
            aria-hidden="true"
            sx={PRICE_DOLLARS_SX}
          >
            {parts.dollars}
          </DtvBox>
          <DtvBox
            component="span"
            aria-hidden="true"
            sx={PRICE_CENTS_SX}
          >
            {parts.cents}
          </DtvBox>
          <DtvBox
            component="span"
            aria-hidden="true"
            sx={PRICE_SUFFIX_SX}
          >
            {suffix}
          </DtvBox>
        </DtvBox>
        {secondLine && (
          <DtvBox
            component="span"
            sx={NEXT_TIER_SX}
          >
            {secondLine}
          </DtvBox>
        )}
      </DtvBox>
    )}
    {/* parts gate: the disclosure qualifies the figure, so without one it is a promotional
        claim with nothing to qualify — "for 12 mos." under no price at all. */}
    {legal && parts && (
      <DtvBox
        component="p"
        sx={LEGAL_SX}
      >
        {legal}
      </DtvBox>
    )}
    {/* parts gate too: a working Shop CTA under a card that shows no figure would still
        read as a purchase offer at an undisclosed price (BK 2026-07-31).
        The second CTA sits inside this gate on purpose: a card that shows no price
        offers no links at all. */}
    {ctaUrl && parts && (
      <DtvBox sx={secondaryCta ? CARD_CTA_ROW_SX : CARD_CTA_SX}>
        <Button
          buttontype="primary"
          mode="light"
          isTangerine
          size="small"
          href={ctaUrl}
          target="_self"
          {...ctaLinkAttrs}
          onClick={onCtaClick}
          sx={secondaryCta ? CARD_CTA_IN_ROW_SX : SHOP_CTA_SX}
        >
          {ctaLabel}
        </Button>
        {secondaryCta && (
          <Button
            buttontype="secondary"
            mode="light"
            isTangerine
            size="small"
            // No `target`: an anchor already opens in this tab, and the Fusion atom does not
            // declare the prop — the primary above it is the repo's only caller that passes one.
            href={secondaryCta.url}
            {...secondaryCta.linkAttrs}
            sx={CARD_CTA_IN_ROW_SX}
          >
            {secondaryCta.label}
          </Button>
        )}
      </DtvBox>
      )}
    </DtvBox>
  );
};

type EntryBannerProps = {
  heading?: string;
  subhead?: string;
  entryImage?: string;
  entryCtaLabel: string;
  /** Authored copy cap, in percent. Falls back to ENTRY_COPY_MAX_PCT_DEFAULT. */
  copyMaxWidthPct?: number | null;
  /** In-page shell: the dark Figma treatment. The modal keeps the light one. */
  inpage?: boolean;
  /** AC1 — the entry CTA's pair. */
  ctaLinkAttrs: Partial<LinkClickAttrs>;
  onStart: () => void;
};

// `?` only when the URL has no query yet. Appending a bare "?imwidth=" to a URL that
// already carries one produces a second "?", which Akamai reads as no width hint at all
// and answers with the 3840px original — the bug already fixed in dtv-marquee.
const withImwidth = (url: string, px: number) => `${url}${url.includes("?") ? "&" : "?"}imwidth=${px}`;

// Page gutter, taken from the content-card block's `margin` object (dtv-content-cards.tsx,
// the non-full-width / non-v13 arm) so this blok's edges land on the same column as the cards
// above and below it. Keys resolve against the DDS DtvTheme (xs 0 / sm 600 / md 1200 /
// lg 1440 / xl 1700), which both routers provide (pages/index.tsx:499,
// src/components/app-router-components/theme/theme.tsx:44).
// Raw px rather than Fusion `space` tokens, and that is a coverage fact, not a preference:
// checked against the installed scale (fusion.themes.fusion/dist/fusion.js:829-852) only
// 20 = spacing06 and 32 = spacing08 exist. 100, 140 and 180 fall between tokens
// (spacing16=96 / spacing17=120 / spacing18=144 / spacing19=168 / spacing20=192), so three of
// the five keys have no token at all and a partly tokenized map would hide the byte-match with
// the reference component. Numbers, not strings, additionally because ENTRY_BANNER_SIZES does
// arithmetic on them (`PAGE_GUTTER_PX.sm * 2`) while a token's value is the string "20px".
const PAGE_GUTTER_PX = {
  xs: 20,
  sm: 32,
  md: 100,
  lg: 140,
  xl: 180
};
// Unchanged from the flat rule this replaced; only the horizontal inset became responsive.
const GUTTER_BLOCK_PX = 24;

// Padding, NOT margin, and on the section root of both shells. This root paints the authored
// `backgroundColor` (adapter maps Storyblok `background_color` —
// lib/adapters/storyblok/ai-product-recommender-adapter.ts:383) and margin sits outside the
// background box, so a margin gutter would band that background instead of full-bleeding it.
// rsn-fee-lookup.tsx is the same shape for the same reason; the content-card box these numbers
// come from has no background, which is why it can use margin.
const PAGE_GUTTER_PADDING = {
  xs: `${GUTTER_BLOCK_PX}px ${PAGE_GUTTER_PX.xs}px`,
  sm: `${GUTTER_BLOCK_PX}px ${PAGE_GUTTER_PX.sm}px`,
  md: `${GUTTER_BLOCK_PX}px ${PAGE_GUTTER_PX.md}px`,
  lg: `${GUTTER_BLOCK_PX}px ${PAGE_GUTTER_PX.lg}px`,
  xl: `${GUTTER_BLOCK_PX}px ${PAGE_GUTTER_PX.xl}px`
};

// Hard cap on how wide the photo may render from DESKTOP_MQ up (WAT-11614 AC-2a).
// The desktop arm gives the image `height: 100%` against the banner's `minHeight`, so its
// width is DERIVED: bannerHeight x 2.376 (the asset is 2376x1000). That makes the growth axis
// the banner's HEIGHT, not the viewport's width. A percentage is the wrong instrument for that
// axis: it only clamps at the banner's OWN width (1160px at a 1440px viewport, 1640px at the
// 2000px page cap), which is far past the designed size, so by the time it binds the photo
// already spans the whole banner with the copy sitting on top of it.
// Measured 2026-08-17 at a 1440px viewport, positive control on the live homepage feed. Both
// columns come off the same element, restoring `max-width: 100%` on it to read the old value
// rather than deriving it (the 600px row is why: bannerHeight x 2.376 predicts 1425.6):
//   banner 360px (today's authored copy) -> 855.36px before, 855.36px after
//   banner 420px (about 1.5 extra lines) -> 997.91px before, nothing clamped it
//   banner 600px                         -> 1160px before, the full banner width
// 900px engages once the banner passes 900 / 2.376 = 378.8px, i.e. 18.8px above today. One
// extra line of authored heading or subhead crosses it, and because the photo is
// right-anchored the extra width grows leftward and pushes the subject further right, which
// is the framing complaint the ticket opens with. Kept alongside the `100%` term rather than
// replacing it, but be clear about what that term does: under today's gutter scale it is
// inert. The narrowest the desktop banner ever gets is 928px, at a 992px viewport where the
// gutter is still `sm` (measured; the `md` step at 1200px gives 1000px), so `min()` always
// picks the 900. It stays only so a future gutter widening that pushes the banner under 900
// cannot overflow. `contain` letterboxes into the banner's matching flat fill if either binds.
// 900 is also the desktop slot ENTRY_BANNER_SIZES advertises, so one constant feeds both and
// the descriptor states the real ceiling instead of an estimate 45px above the render.
const DESKTOP_IMAGE_MAX_PX = 900;

// Horizontal focal point for the `cover` crop below DESKTOP_MQ (WAT-11614 AC-2b). There the
// banner has no reserved height, so the copy's line count sets the box aspect and `cover`'s crop
// follows it; the subject is at the right of the 2376x1000 asset, so the default 50% centres the
// crop and spends the clipping on his side.
// Controlled A/B on ONE local build, `object-position` toggled on the same element (it is
// paint-only: the box measured identical under both values at every width below). `window` is the
// slice of the ASSET's width that survives the crop; the head band is 69.4-85.4% of that width,
// from column-scanning the JPEG:
//   viewport  box          h-window 50%  h-window 65%  head shown, 50% -> 65%
//   320       280x267.19   27.9-72.1     36.3-80.4     16.9% -> 68.8%
//   375       335x267.19   23.6-76.4     30.7-83.5     43.8% -> 88.1%   (photo moves 45px)
//   390       350x238.39   19.1-80.9     24.8-86.6     71.9% -> 100%
//   430       390x238.39   15.6-84.4     20.2-89.1     93.8% -> 100%
//   540       500x217.59    1.6-98.4      2.1-98.8     100%             (moves 2.6px)
//   600       536x227.20    0.4-99.6      0.5-99.8     100%             (moves 0.6px)
//   768       704x227.20   none          none          100%
// So the shift earns its keep below ~430px and is visually inert above it: the head is whole
// either way there, and QA at 768px will correctly see no change at all.
// Horizontal only. The Y term stays at the browser default, so `cover` still clips the photo's
// HEIGHT wherever the box is wider in aspect than 2.376 (23% of it at 768px) — untouched by this
// change, and the desktop arm switches to `contain` anyway.
// The h-crop does not simply end above 600px; the band is not monotonic, because two things step
// at the DDS sm breakpoint at once. Measured, one-line heading: 540 crops (2.298), 560 clean
// (2.390), 580 clean (2.482), 600 crops AGAIN (2.359), 610 clean (2.403). At 600 the gutter goes
// 20 -> 32px a side (banner 540 -> 536) while the heading's type goes 24 -> 32px, which is the
// whole 217.59 -> 227.20 height step; vertical padding is 72px on both sides of the breakpoint.
// A longer authored heading reopens it regardless: forced to 3 lines at 600px the window drops to
// 12.9-87.1 at 50% / 16.8-91.0 at 65%, head still 100% either way. At 768px even a wrapped
// heading stays uncropped (2.651).
// prod 2.98.1 is NOT a control for this constant: its banner is 8px wider at each width (it has no
// PAGE_GUTTER_PADDING), so its windows differ from the 50% column above — 375px reads 23.0-77.0
// there, not 23.6-76.4. Compare prod for "what ships today", never for the effect of 65 vs 50.
// 65 and not more: a bigger shift pulls the subject toward the copy. See the copy-cap comment
// below for the measured clearance; 75 overlaps the heading at 320-430px.
// 65 only re-aims the crop, so a longer authored heading still tightens the framing. Reserving
// the banner's aspect below DESKTOP_MQ is what would decouple the two outright; that changes a
// design value and is a separate ticket.
// TRAP: 65 belongs to THIS photo — same reason ENTRY_COPY_MAX_PCT_DEFAULT is CMS authorable.
// Swapping the asset is a CMS action and must move this number too, which today means a code
// change and a deploy. Follow-up, in preference order, with the coordinates so it does not
// evaporate:
//   1. Storyblok assets can carry their own focus point, which would travel WITH the asset and
//      remove the coupling outright. In `ai-product-recommender-adapter.ts` the asset object is
//      still whole where `entryImage` is mapped (~:368) and only `entry_image.filename` is kept;
//      nothing under lib/adapters reads `.focus` today. Whether a focus point is actually
//      authored on this asset is UNVERIFIED — the delivered props carry a bare URL string, so it
//      cannot be read off the page; fetch the raw blok before designing on it.
//   2. Failing that, mirror `entry_copy_max_width_pct` as `entry_image_focus_x_pct`: copy the
//      `entryCopyMaxWidthPct` coercion + `entryCopyMaxWidthMessages` warn helper in that same
//      adapter and add one line where the props are assembled.
export const ENTRY_IMAGE_FOCUS_X_PCT = 65;

// Below the desktop breakpoint the banner spans the root's content box, so the slot is the
// viewport minus the gutter on both sides — not 100vw. Every number here is derived from the
// constants that actually set the layout; a transcribed one would drift, and a wrong descriptor
// surfaces only as the CDN sending a file that does not match the slot.
const ENTRY_BANNER_SIZES = [
  `(min-width: ${DESKTOP_MIN_PX}px) ${DESKTOP_IMAGE_MAX_PX}px`,
  `(min-width: ${MOBILE_WIDTH}px) calc(100vw - ${PAGE_GUTTER_PX.sm * 2}px)`,
  `calc(100vw - ${PAGE_GUTTER_PX.xs * 2}px)`
].join(", ");

// The in-page section has no width cap of its own — it spans the page container — so its slot
// follows the same page gutters as the container: 100/140/180px a side from 1200/1440/1700px.
const INPAGE_BANNER_SIZES = [
  `(min-width: 1700px) calc(100vw - ${PAGE_GUTTER_PX.xl * 2}px)`,
  `(min-width: 1440px) calc(100vw - ${PAGE_GUTTER_PX.lg * 2}px)`,
  `(min-width: 1200px) calc(100vw - ${PAGE_GUTTER_PX.md * 2}px)`,
  `(min-width: ${MOBILE_WIDTH}px) calc(100vw - ${PAGE_GUTTER_PX.sm * 2}px)`,
  `calc(100vw - ${PAGE_GUTTER_PX.xs * 2}px)`
].join(", ");

// xs hands 4px per side back inside the card, which keeps the copy's content width exactly
// where production had it: production was 16px of root padding + 24px here, this is 20px of
// page gutter + 20px here, both 40px per side. Without the giveback the heading cap lands 1px
// under what one line needs at 390px, and wrapping costs more than a line: the card reserves
// no height below DESKTOP_MQ, so a second line makes it taller and the `objectFit: cover`
// photo zooms in far enough to crop the subject's face.
// Measured 2026-08-17 in Chrome, at the fonts that actually render (the h2's own computed font
// is the fallback; the inner DtvText span carries PFDinMed):
//   390px  heading cap 217 vs 212 needed → one line. At 24px here: 211 vs 212 → two lines.
//   600px  the sm floor, so sm's tightest cap. heading 342 vs 283, subhead 342 vs 287, both
//          one line, and the banner is 227px tall either side of the change — so sm does not
//          need a giveback even though its cap fell 364 → 342.
// Reserving the banner height below DESKTOP_MQ would decouple wrapping from the crop outright
// and make this giveback unnecessary; that changes a design value and is not in this change.
const ENTRY_BANNER_PADDING = {
  xs: "28px 20px 44px",
  sm: "28px 24px 44px"
};

// Both banner shells, hoisted in pairs so each difference is visible in one place and the
// modal's objects stay exactly what they were. Figma 7190:841 / 7190:1120 for the in-page
// arm: navy card, white copy, the channel collage bleeding off the right edge.
const ENTRY_ROOT_MODAL_SX = {
  position: "relative",
  borderRadius: "14px",
  overflow: "hidden"
};
const ENTRY_ROOT_INPAGE_BASE_SX = {
  position: "relative",
  borderRadius: "8px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  // Navy under both variants: the cover image hides it once loaded, but a failed image no
  // longer leaves white copy on a transparent box. Figma navy; no DDS palette token.
  backgroundColor: "#1b242e",
  [INPAGE_TABLET_MQ]: {
    minHeight: "500px"
  },
  [DESKTOP_MQ]: {
    border: `1px solid ${colorPalette?.gray?.[300]}`
  }
};
// With an image, the asset is the backdrop — designers cut it with the left empty for the copy
// (BK 2026-09-03). The phone-only dim stays: the copy is white.
const ENTRY_ROOT_INPAGE_SX = {
  ...ENTRY_ROOT_INPAGE_BASE_SX,
  "&::after": {
    content: "''",
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.8)",
    pointerEvents: "none",
    [DESKTOP_MQ]: {
      display: "none"
    }
  }
};
// No image: add the navy gradient glow on top of the base fill.
const ENTRY_ROOT_INPAGE_NO_IMAGE_SX = {
  ...ENTRY_ROOT_INPAGE_BASE_SX,
  backgroundImage: "radial-gradient(circle 710px at 100% 100%, rgba(106,120,138,0.57), rgba(106,120,138,0))"
};

const ENTRY_IMAGE_MODAL_SX = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: `${ENTRY_IMAGE_FOCUS_X_PCT}% 50%`,
  // The banner window widens toward ~5.5:1 at 2000px while the asset is
  // 2.376:1, so cover here was discarding over half the photo vertically —
  // the subject lost both the top of his head and the hand (BK, 2026-08-05).
  // Right-anchor the uncropped photo instead and fade its left edge into the
  // banner's matching fill so no seam shows. The px term is the live ceiling: it
  // caps growth driven by the banner getting taller (see DESKTOP_IMAGE_MAX_PX —
  // that is the one AC-2a asks for). The `100%` term is inert at today's gutters
  // (narrowest desktop banner 928px) and is only a guard against a future gutter
  // change. contain letterboxes into the matching fill if either binds.
  [DESKTOP_MQ]: {
    left: "auto",
    right: 0,
    width: "auto",
    maxWidth: `min(100%, ${DESKTOP_IMAGE_MAX_PX}px)`,
    objectFit: "contain",
    objectPosition: "right center",
    WebkitMaskImage: "linear-gradient(to right, transparent, #000 48px)",
    maskImage: "linear-gradient(to right, transparent, #000 48px)"
  }
};

// Full-bleed at every width. Designers cut the asset with its subject on the right and the
// left empty for the copy, so every crop keeps the right edge and gives up the left.
const ENTRY_IMAGE_INPAGE_SX = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "100% 50%"
};

const ENTRY_COPY_MODAL_SX = {
  position: "relative",
  padding: ENTRY_BANNER_PADDING,
  [DESKTOP_MQ]: {
    padding: "76px 96px",
    minHeight: "360px"
  }
};
// zIndex, because the root's dimming ::after paints after this box. No minHeight here — the
// root owns the banner height in this shell.
const ENTRY_COPY_INPAGE_SX = {
  position: "relative",
  zIndex: 1,
  padding: "48px 24px 64px",
  [DESKTOP_MQ]: {
    padding: "48px 88px"
  }
};
// The copy-width cap (entry_copy_max_width_pct) is a modal-banner knob (PRD AC-1b). The in-page
// copy uses the fixed Figma widths; below 992px the image sits behind an 80% dim, so the copy
// may run the full width.
const ENTRY_HEADING_INPAGE_SX = {
  margin: "0 0 8px",
  [DESKTOP_MQ]: {
    marginBottom: "9px",
    maxWidth: "385px"
  }
};
// Figma "Mobile/Headline 3" is 28px. Inside a media query on purpose: DtvTypography emits the
// variant's own breakpoint sizes after a plain sx declaration, so only an at-rule of ours wins.
const ENTRY_HEADING_TEXT_INPAGE_SX = {
  color: colorPalette?.white?.main,
  [BELOW_DESKTOP_MQ]: {
    fontSize: "28px",
    lineHeight: 1.2
  },
  [DESKTOP_MQ]: {
    fontSize: "36px"
  },
  ...PIN_36
};
// Sizes come from `sx` rather than a DtvText variant: no installed variant is 24px PF DIN from
// the breakpoint up AND small enough on a phone, and a variant's own media rules would beat
// these anyway (see the note above pinFontSize).
const ENTRY_SUBHEAD_INPAGE_SX = {
  display: "block",
  color: colorPalette?.white?.main,
  fontFamily: "roboto-regular",
  fontSize: "16px",
  lineHeight: 1.3,
  marginBottom: "16px",
  [DESKTOP_MQ]: {
    fontFamily: "PFDinReg",
    fontSize: "24px",
    lineHeight: 1.3,
    marginBottom: "22px",
    maxWidth: "330px"
  }
};

// Modal-mode entry point: the marquee that opens the quiz modal.
// Per Figma (6976:2162) the copy sits on the left with the photo's subject on the
// right, over a light backdrop. Below DESKTOP_MQ the photo covers the banner
// full-bleed (the narrow window crops backdrop, not subject). From DESKTOP_MQ up the
// banner is wider than the 2.376:1 asset, so cover would crop the subject vertically
// instead — there the uncropped photo is right-anchored over a matching flat fill.
// Solid fill is the no-image fallback.
const EntryBanner = ({
  heading,
  subhead,
  entryImage,
  entryCtaLabel,
  copyMaxWidthPct,
  inpage,
  ctaLinkAttrs,
  onStart
}: EntryBannerProps) => {
  // Only bind the percentage while there is a photo to avoid — the solid-fill fallback has
  // nothing in the way, so capping there would wrap the copy early for no reason.
  const pct = copyMaxWidthPct == null ? ENTRY_COPY_MAX_PCT_DEFAULT : copyMaxWidthPct;
  const copyMaxWidth = (px: string) => (entryImage ? `min(${pct}%, ${px})` : px);
  return (
  <DtvBox
    sx={inpage ? (entryImage ? ENTRY_ROOT_INPAGE_SX : ENTRY_ROOT_INPAGE_NO_IMAGE_SX) : {
      ...ENTRY_ROOT_MODAL_SX,
      backgroundColor: entryImage ? ENTRY_BANNER_BACKDROP : colorPalette?.lapis?.light
    }}
  >
    {entryImage && (
      <DtvBox
        component="img"
        src={withImwidth(entryImage, 1200)}
        // The candidates let the CDN send a phone-sized file to a phone and a 2x file to a
        // retina desktop (2000px is the widest viewport we support), matching how the other
        // video-lab banners request images (dtv-content-cards.tsx:1016,
        // video-lab/conspiracy-board/components/marquee.tsx:59).
        srcSet={`${withImwidth(entryImage, 700)} 700w, ${withImwidth(entryImage, 1200)} 1200w, ${withImwidth(entryImage, 2000)} 2000w`}
        sizes={inpage ? INPAGE_BANNER_SIZES : ENTRY_BANNER_SIZES}
        alt=""
        sx={inpage ? ENTRY_IMAGE_INPAGE_SX : ENTRY_IMAGE_MODAL_SX}
      />
    )}
    <DtvBox sx={inpage ? ENTRY_COPY_INPAGE_SX : ENTRY_COPY_MODAL_SX}>
      {/* The subject sits on the right of the full-bleed image, so on a phone the copy
          ran straight across his face: at a 500px viewport the padded content box is
          only 420px, so the authored 460/380px caps never bind and the text took the
          full width. Scanning the rendered image for its first dark column puts the
          man's hair at 73.9% across that box, so 70% is the widest round cap that still
          clears him. (That 73.9% is the PRE-shift position; see the re-measurement below.)
          RE-MEASURED 2026-08-17 after ENTRY_IMAGE_FOCUS_X_PCT moved the crop: the two are
          coupled, because re-aiming the crop right slides the subject toward this copy.
          Same 500px viewport, same first-dark-column method, hair position in the 420px
          content box and the air left against the 70% cap's right edge (294px):
            focus 50% -> hair 310px (73.9%), air 16px
            focus 65% -> hair 302px (71.8%), air  8px
          Worst case across the phone band, cap-filling heading, one and two lines
          (measured, local build): 320px +22, 375px +19, 390px +15, 430px +13, 500px +8,
          540px +6, 600px +7. Never negative, including with the banner grown to 246px by a
          wrapped heading. Single-digit clearance at 500-600px predates this change (prod is
          +8 at 540px); the 2px the focal shift costs there does not.
          The AUTHORED value is the exposed edge, and the shift moved that edge by two points:
          overlap starts once the cap exceeds hair/420, i.e. above 73.8% before and above 71.9%
          now, so an authored 72 or 73 newly overlaps him. 74 and up already did, and the adapter
          accepts 1-100 either way, so the class is not new — only its boundary moved.
          Raise either constant and re-run that sweep; the default is pinned in the spec.
          That 71.8% belongs to ONE photo and `objectFit: cover` re-crops it as the banner
          narrows, so the cap is authorable (`entry_copy_max_width_pct`) rather than
          hard-coded: swapping the asset is a CMS action, and the number that follows it
          should be too. Unset falls back to the measured 70.
          min() leaves the authored px widths alone — the % term only wins while the box
          is narrower than the px value, i.e. below ~657px (heading) / ~543px (subhead)
          of content box at the default 70, so the desktop layout is untouched and the
          phone band is the one that changes.
          Wrapping around the real silhouette (shape-outside) would remove the coupling
          entirely but needs a cut-out asset with an alpha channel, which today's image
          is not. */}
      {heading && (
        <DtvBox
          component="h2"
          sx={inpage ? ENTRY_HEADING_INPAGE_SX : {
            margin: "0 0 8px",
            maxWidth: copyMaxWidth("460px")
          }}
        >
          {/* Pinned for the same reason as the question and results headings:
              subhead1medium keeps climbing above 1440 (40px there, 44px at 1700+),
              so a wide monitor rendered a heading two steps larger than any other
              in this component. 36px is the size the other three settle on, which
              makes every heading the component owns one size on desktop. Not
              Figma-derived (BK 2026-07-31 said to skip the comp check here) —
              swap the argument if the comp says otherwise. */}
          <DtvText
            content={heading}
            variant="subhead1medium"
            sx={inpage ? ENTRY_HEADING_TEXT_INPAGE_SX : PIN_36}
          />
        </DtvBox>
      )}
      {subhead && (
        <DtvText
          content={subhead}
          sx={inpage ? ENTRY_SUBHEAD_INPAGE_SX : {
            display: "block",
            marginBottom: "20px",
            maxWidth: copyMaxWidth("380px"),
            color: colorPalette?.gray?.[300]
          }}
        />
      )}
      <DtvBox sx={inpage ? ENTRY_CTA_WRAP_INPAGE_SX : undefined}>
        <Button
          buttontype="primary"
          mode="light"
          isTangerine
          size="medium"
          {...ctaLinkAttrs}
          onClick={onStart}
          sx={inpage ? ENTRY_CTA_INPAGE_SX : ENTRY_CTA_SX}
        >
          {entryCtaLabel}
        </Button>
      </DtvBox>
    </DtvBox>
  </DtvBox>
  );
};

const AiProductRecommender = (props: AiProductRecommenderProps) => {
  const {
    id,
    entryMode = "modal",
    heading,
    subhead,
    entryImage,
    entryCtaLabel = "Get started",
    entryCopyMaxWidthPct,
    resultsHeading,
    nextLabel = "Next",
    backLabel = "Back",
    closeLabel = "Close",
    selectOneLabel = "Select one",
    selectAllLabel = "Select all that apply",
    resultsCtaLabel = "See my results",
    shopCtaLabel = "Shop now",
    primaryTagLabel = "Our pick for you",
    startOverLabel = "Start over",
    priceLegal,
    priceCopy,
    recipe,
    catalog,
    messages,
    backgroundColor,
    secondaryCtaSignatureLabel,
    secondaryCtaSignatureUrl,
    secondaryCtaGenreLabel,
    secondaryCtaGenreUrl
  } = props;

  // Only the inline variant opts out, matching how the CMS value is normalized upstream
  // (normalizeEntryMode). Testing for "modal" instead would send any unrecognized value
  // to inline — i.e. the tool rendering straight onto the page — despite modal being the
  // declared default. strict mode is off, so a bad string can reach this at runtime.
  const isModal = entryMode !== "inline";
  // Mounted with the banner, so it has settled long before the card can render.
  const inpageWide = useMinWidth(INPAGE_TABLET_QUERY, !isModal);

  const errors = useMemo(
    () => (messages || []).filter(m => m.level === "error"),
    [messages]
  );
  const unusable = !recipe || !recipe.questions || recipe.questions.length === 0 || !catalog || errors.length > 0;

  const ssKey = storageKey(id);
  // useId, not a literal fallback: the adapter is allowed to return `id: undefined`, and the
  // whole point of scoping is that two bloks on one page never mint the same element id.
  // It is SSR-safe by design — that is what the hook exists for — so it does not desync
  // hydration the way a random suffix would.
  const generatedScope = useId();
  const idScope = id || generatedScope;
  const [modalOpen, setModalOpen] = useState(false);
  // In-page: the banner CTA swaps the quiz card in, in place, and the banner never returns —
  // Start over goes back to Q1 inside the card.
  const [inlineStarted, setInlineStarted] = useState(false);
  const [phase, setPhase] = useState<"ask" | "results">("ask");
  const [answers, setAnswers] = useState<Answers>({});
  const [cursor, setCursor] = useState(0);
  const prevCursorRef = useRef<number | null>(null);
  // In-page only, armed by the banner CTA: that click unmounts the button it came from, so
  // the card would otherwise open with focus on <body>. The modal manages its own focus, and
  // a restored results card was never clicked into.
  const focusOnOpenRef = useRef(false);

  // State, set only by the restore effect: analytics must not describe the phantom first
  // paint. Restore has to happen in an effect (reading sessionStorage during render would
  // desync the server's markup from the client's first render and break hydration), so the
  // first commit ALWAYS paints the ask phase at cursor 0 with empty answers — even for a
  // session that already finished. A ref set by the restore effect could not gate that out:
  // it would already be true by the time the effects below it ran; only a re-render can.
  const [restored, setRestored] = useState(false);

  // Restore a same-session run (refresh / route back) — but only a COMPLETED one; a quiz
  // abandoned mid-flow starts over. Validate the whole row, not just the discriminant:
  // sessionStorage is writable by any same-origin script and by older builds, so `answers`
  // is exactly as untrusted as `phase`. Anything else — mid-quiz rows from older builds,
  // malformed answer shapes — falls through to the purge instead of being replayed.
  useEffect(() => {
    const saved = readSession(ssKey);
    const savedAnswers = saved?.answers;
    // At least one entry: a completed run always answered its final visible question, so an
    // empty answers object is not a row this build wrote — without the key-count check the
    // .every() below is vacuously true and `{}` would replay as a results screen. Individual
    // entries may still be [] (pick, unpick, then the question hides via show_if), so only
    // the key count is tightened, not the values.
    const usable = saved?.phase === "results" &&
      !!savedAnswers && typeof savedAnswers === "object" && !Array.isArray(savedAnswers) &&
      Object.keys(savedAnswers).length > 0 &&
      Object.values(savedAnswers).every(v => Array.isArray(v) && v.every(x => typeof x === "string"));
    if (usable) {
      setAnswers(savedAnswers as Answers);
      setPhase("results");
      // modalOpen is intentionally never restored — see StoredSession.
    } else if (saved) {
      clearSession(ssKey);
    }
    setRestored(true);
  }, [ssKey]);

  // Persist on state changes. Only the results phase is written: mid-quiz progress must not
  // outlive the visit, in either entry mode — leaving the modal or the page mid-quiz starts
  // the run over. The same gate protects a saved row from the first run after mount, whose
  // closure always still holds the initial phase "ask" (setState is async) — re-add an init
  // guard if ask-phase writes ever return.
  useEffect(() => {
    if (phase !== "results") {
      return;
    }
    writeSession(ssKey, {
      answers,
      phase
    });
  }, [ssKey, answers, phase]);

  const visible = useMemo(
    () => (unusable ? [] : visibleQuestions(recipe as Recipe, answers)),
    [unusable, recipe, answers]
  );
  // A recipe whose every question starts hidden (show_if chains that can never fire) has
  // nothing to ask — the banner CTA would open an empty dialog. Same contract as `unusable`:
  // hide the component. Evaluated against NO answers so mid-session answer changes can never
  // flap it; it cannot fold into `unusable` itself because `visible` derives from that flag.
  const askable = useMemo(
    () => (unusable ? false : visibleQuestions(recipe as Recipe, {}).length > 0),
    [unusable, recipe]
  );
  const clampedCursor = Math.max(0, Math.min(cursor, visible.length - 1));
  const current = visible[clampedCursor];
  const isLast = clampedCursor === visible.length - 1;
  // ONE string for the on-screen step indicator and for the analytics number token
  // (Jay 2026-08-06: report "2 of 4", not "2"). Deriving them separately would let the
  // report disagree with what the shopper saw — and the total genuinely moves, because a
  // conditional question appearing takes a run from "2 of 3" to "2 of 4" mid-quiz.
  const progressLabel = `${clampedCursor + 1} of ${visible.length}`;

  // run() throws on catalog-contract breaches (e.g. every signature excluded, or the
  // live-sports anchor pointing at an excluded signature). This memo runs during render and
  // the app has no error boundary above this component, so an uncaught throw here unmounts
  // the whole React root — downgrade a breach to the unusable contract instead: hide. The
  // memo only records the failure; logging and session cleanup live in the effect below
  // (a memo body must stay side-effect free — StrictMode/concurrent renders replay it).
  const engineOutcome = useMemo<{ result: RunResult | null; failed: boolean; error?: unknown }>(() => {
    if (unusable || phase !== "results") {
      return {
        result: null,
        failed: false
      };
    }
    try {
      return {
        result: run(answers, {
          recipe: recipe as Recipe,
          catalog: catalog as Catalog
        }),
        failed: false
      };
    } catch (e) {
      return {
        result: null,
        failed: true,
        error: e
      };
    }
  }, [unusable, phase, answers, recipe, catalog]);
  const { result, failed: engineFailed, error: engineError } = engineOutcome;

  // The cards the results screen will render, in display order. Hoisted out of
  // renderResults so the AC10 event reports exactly what is on screen — deriving the
  // reported ids from a second copy of this logic would let the event and the UI drift.
  // Returns [] whenever there is no result, which is also every render before the
  // early return below, so the hook order is unconditional.
  const orderedCards = useMemo(() => {
    if (!result) {
      return [];
    }
    // `result.broad` is the very object adaptCatalog put in catalog.signatures (the
    // engine filters and sorts but never copies), so the display fields ride along.
    const broadPack = catalog?.signatures?.find(s => s.id === result.broad?.id) ??
      (catalog?.spanishSignature?.id === result.broad?.id ? catalog?.spanishSignature : null);
    const broadCard = {
      key: "broad",
      title: result.broadLabel,
      price: result.broadPrice,
      regularPrice: result.broad ? result.broad.regularPrice : null,
      ...cardMetaOf(broadPack),
      ctaUrl: buildBroadCtaUrl(result.broad),
      // AC10's own example id (`choice-package`) is this slug form.
      analyticsId: broadItemId(result.broad?.slug)
    };
    // RunResult exposes `broad` as a full CatalogPackage but only ids/labels for the
    // focused pack, so the pre-promo price comes off the catalog entry instead.
    // `[0]` is safe because the engine only ever emits 0 or 1 focused id (engine.ts:316
    // and :535 are the only assignments). If multi-pick is ever opened up, the title
    // already joins labels but `regularPrice`/`channels` would still describe pack one
    // only — sum them (like sumP does for price) or drop them for the multi case.
    // `analyticsId` already handles the multi case: it joins every code it is given.
    const focusedPack = catalog?.genrePacks?.find(g => g.id === result.focusedIds[0]);
    const focusedCard = {
      key: "focused",
      title: result.focusedLabels.join(" + "),
      price: result.focusedPrice,
      regularPrice: focusedPack?.regularPrice ?? null,
      ...cardMetaOf(focusedPack),
      ctaUrl: buildFocusedCtaUrl(result.focusedIds, catalog as Catalog),
      analyticsId: focusedItemId(
        result.focusedIds.map(fid => catalog?.genrePacks?.find(g => g.id === fid)?.shopCode)
      )
    };
    // One card by default (2026-07-21 feedback #2): the engine's showAlternative gates the
    // runner-up. English lane: a near-tie on price only. Spanish lane (frozen) also
    // force-shows on its bundle-guard / coverage rules. Contract: never a card without a live price —
    // the engine never leads with an unpriced focused card, and showAlternative is
    // false whenever either price is unknown; alt.price is a belt-and-braces guard.
    // Number.isFinite, not `!= null`: ResultCard skips its whole price block for a
    // non-numeric price, so `!= null` here would admit a runner-up card showing a title
    // and a CTA with no figure at all — the one thing this line exists to prevent.
    const lead = result.primary === "focused" ? focusedCard : broadCard;
    const alt = result.primary === "focused" ? broadCard : focusedCard;
    return result.showAlternative && Number.isFinite(alt.price) ? [lead, alt] : [lead];
  }, [result, catalog]);

  // AC10 payload, in display order. Derived from the rendered cards (not recomputed) and
  // memoised so the effect below has a stable dependency instead of a fresh array each render.
  const recommendedItemIds = useMemo(
    () => orderedCards.map(card => card.analyticsId),
    [orderedCards]
  );

  // What the results screen is called, resolved ONCE for both consumers: the visible <h2>
  // and the dialog name. They have to agree — a whitespace-only CMS value is not a title, so
  // resolving it separately let the untrimmed <h2> render blank while the dialog fell through
  // to `heading`, leaving a screen reader announcing a title that is not on screen.
  const resultsTitle = (resultsHeading || "").trim() || (heading || "").trim();

  // Dialog name follows the screen the shopper is on, otherwise a screen reader announces the
  // quiz title over the results.
  const dialogLabel = phase === "results" ?
    resultsTitle :
    (heading || "").trim() || (resultsHeading || "").trim();

  // The single predicate for "this component puts anything on screen". The render early
  // return below tests exactly this, so "visible to analytics" and "actually rendered" cannot
  // drift — the whole point of naming it rather than spelling the condition twice.
  //
  // No event escapes through the difference today, which is worth recording because it is not
  // obvious: `askable === false` is absorbing — visibleQuestions accumulates `kept` top-down
  // from VISIBLE questions only, so a chain that starts hidden with no answers stays hidden
  // for every answer set, leaving `current` undefined and `quizVisible` false anyway. And
  // `engineFailed` only arises at phase "results", where it forces `result: null` and closes
  // `resultsVisible` the same way. Both are properties of other modules, so the gate does not
  // rely on them holding.
  const renderable = !unusable && askable && !engineFailed;

  // The in-page card is up once the shopper starts, and immediately for a restored completed
  // run — that lands on its results with no banner left to click.
  const inpageOpen = inlineStarted || (restored && phase === "results");
  // "the shell that holds the quiz is on screen", which is what both gates below need.
  const shellOpen = isModal ? modalOpen : inpageOpen;

  // The quiz is on screen when its shell is. Used to gate the step-focus effect.
  const quizVisible = renderable && phase === "ask" && !!current && shellOpen;

  // The results screen is on screen when the run finished AND its container is up.
  // Modal mode gates on modalOpen because a restored session can carry phase "results"
  // while the modal stays shut (StoredSession never restores modalOpen) — nothing is
  // rendered then, and AC9/AC10 describe a screen the shopper can actually see. In-page has
  // no such gap: `inpageOpen` is what puts a restored results card on the page.
  const resultsVisible = renderable && phase === "results" && !!result && shellOpen;

  // ---- WAT-11357 analytics, revised by WAT-11931 ------------------------------------
  // Declaration order IS firing order: React runs effects in the order they are declared,
  // so one modal open emits AC2 (page_view) -> AC3 (start) -> AC4 (displayed), the
  // sequence the ticket narrates. All of these ride the app's single post-paint analytics
  // queue (see utils/analytics.ts), which preserves relative order when defer is on.
  // AC numbers here are WAT-11931's, which renumbered WAT-11357's.

  // Primitives, not `current`: `visible` is rebuilt on every answer change, so the
  // question object gets a new identity whenever an option is toggled. Keying the AC4
  // effect on the object would re-fire "displayed" on every click.
  const currentQuestionId = current?.id;
  const currentQuestionLabel = current?.label;

  // AC2 — the shopper opening the quiz, MODAL ONLY: a modal open is a new overlay context,
  // while the in-page card only expands what is already on the page, so it must fire nothing
  // here. Keyed on the open flag alone so one open emits one page_view, not one per re-render.
  // A restored completed run is not an open — StoredSession never restores modalOpen.
  const modalOpenedByShopper = isModal && modalOpen;
  useEffect(() => {
    if (!modalOpenedByShopper) {
      return;
    }
    fireQuizPageView(quizVirtualUrl());
  }, [modalOpenedByShopper]);

  // AC3 — quiz start: ONE PER STRETCH OF THE QUIZ BEING ON SCREEN. Read that literally, and
  // do NOT restate it as "one per attempt": it is not deduplicated across a close and reopen.
  // Closing at Q2 and reopening emits a second `start` (measured: ['start','start']), and
  // that close also discards the run, so the second one opens a genuinely new attempt back
  // at "1 of 2". Anyone assuming a 1:1 pairing with AC9 will still be wrong: the shopper
  // who abandons leaves a start with no complete behind.
  //
  // Four cases the predicate covers, and WHO decided each. The split matters: two are Jay's
  // stated rule, two are our reading of it, and only the first pair carries his sign-off.
  //   modal open landing on a question   -> fires.   JAY, 2026-08-06 Q6 (counts modal opens)
  //   modal open landing on RESULTS      -> nothing. JAY, same answer; quizVisible is false
  //   mid-quiz close then reopen         -> fires.   OURS. A reopen is literally a modal open,
  //                                        so his rule covers it, but he was never shown this
  //                                        scene. Unconfirmed; ask before trusting start-rate
  //                                        for sessions that bounce.
  //   Start over                         -> fires.   OURS, see the pairing argument below
  //
  // Start over is in because `start` and `complete` have to stay countable against each other.
  // Re-arming only on the modal closing was tried and measured: it emits start/complete/complete
  // for one open that completes, restarts and completes again, since handleNext re-arms
  // pendingCompleteRef on every last-step click. A completion rate above 100% is worse than a
  // start the spec did not explicitly ask for. This gives 2:2 there and 2:1 when the second
  // attempt is abandoned.
  //
  // Identical in both placements per AC3, and its "not on page load" clause holds for free:
  // the restore effect only ever replays phase "results", so no question renders on a load.
  // Only three events are placement-specific — AC2 and AC7's close (modal), AC12 (in-page).
  const startFiredRef = useRef(false);
  useEffect(() => {
    if (!restored || !quizVisible) {
      startFiredRef.current = false;
      return;
    }
    if (startFiredRef.current) {
      return;
    }
    startFiredRef.current = true;
    fireQuizStatus("start");
  }, [restored, quizVisible]);

  // Latest progress label, READ by the events below rather than depended on. A conditional
  // question appearing takes the run from "2 of 3" to "2 of 4" without the shopper navigating,
  // so keying the AC4 effect on the label would re-fire "displayed" on an option click and
  // inflate every question's impression count. Declared before that effect so the same commit
  // updates this first.
  const progressLabelRef = useRef(progressLabel);
  useEffect(() => {
    progressLabelRef.current = progressLabel;
  }, [progressLabel]);

  // AC4 — any question rendering. Keyed on question id AND step index so that navigating
  // (including Back to an already-seen question, Jay Q8: yes, that re-fires) reports again,
  // while merely selecting an option — which rebuilds `visible` and can insert a conditional
  // question — does not.
  useEffect(() => {
    if (!restored || !quizVisible || !currentQuestionId) {
      return;
    }
    fireQuizQuestionStatus("displayed", progressLabelRef.current, currentQuestionLabel);
  }, [restored, quizVisible, currentQuestionId, currentQuestionLabel, clampedCursor]);

  // AC9 — armed by handleNext only, i.e. by a completion the shopper actually drove in this
  // session (Jay Q6). A results screen restored from sessionStorage, or one revealed by
  // reopening the modal, re-renders the recommendation without completing anything, and
  // counting those would inflate the completion rate against a start that never repeated.
  const pendingCompleteRef = useRef(false);

  // AC9 then AC10, in that order — the ticket requires `complete` to precede the
  // recommendation event. AC10 is an impression, so it reports on EVERY results render
  // (restored or reopened); AC9 only when armed. The ref resets when the screen leaves.
  const resultsFiredRef = useRef(false);
  useEffect(() => {
    if (!restored || !resultsVisible) {
      resultsFiredRef.current = false;
      return;
    }
    if (resultsFiredRef.current) {
      return;
    }
    resultsFiredRef.current = true;
    if (pendingCompleteRef.current) {
      pendingCompleteRef.current = false;
      fireQuizStatus("complete");
    }
    fireQuizRecommendationStatus(recommendedItemIds);
  }, [restored, resultsVisible, recommendedItemIds]);

  // closeModal runs from DtvModal's own Esc/backdrop handler, which DtvModal binds ONCE with
  // [] deps (dds.ui.dtv-modal/dist/dtv-modal.js:38-50) — so it is the closure from the render
  // that mounted the modal. Reading `phase` straight out of closeModal therefore describes the
  // step the shopper was on when they opened the quiz, and the discard branch would wipe the
  // recommendation they are looking at. Assigned in an effect rather than during render so a
  // concurrent re-render cannot tear it.
  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // The destination half of a pair rendered in the first paint is derived from `window`, so no
  // pair is rendered until mount: SSR markup would disagree with the client, and a pair whose
  // destination is still empty makes GTM report the click with an empty value instead of falling
  // through. Attributes are read by GTM off the DOM, so the value is necessarily the one from
  // the last render; nothing here subscribes to the router, so a shallow route change would
  // leave it behind. The results screen's pairs are exempt from the gate: that screen only
  // exists after an interaction, so it is never in the first paint.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const pairWhenMounted = (
    linkName: string,
    linkPosition: string,
    linkDestination: () => string
  ): Partial<LinkClickAttrs> => {
    if (!mounted) {
      return {};
    }
    return linkClickAttrs(linkName, linkPosition, linkDestination());
  };

  // Move focus to the new question on step changes, and on the one first paint that is a
  // shopper action rather than a page load — the in-page card opening from its CTA. Every
  // other first paint is still skipped on purpose (see focusOnOpenRef).
  useEffect(() => {
    if (!quizVisible) {
      return;
    }
    const openedByCta = focusOnOpenRef.current;
    if (openedByCta || (prevCursorRef.current !== null && prevCursorRef.current !== clampedCursor)) {
      focusOnOpenRef.current = false;
      const el = document.getElementById(domId(idScope, "q", (current as RecipeQuestion).id));
      if (el) {
        el.focus();
      }
    }
    prevCursorRef.current = clampedCursor;
  }, [quizVisible, clampedCursor, current]);

  useEffect(() => {
    if (unusable && (errors.length > 0 || catalog === null)) {
      console.warn("[AiProductRecommender] hidden — config/catalog errors", errors);
    }
  }, [unusable]);

  // Authoring diagnostic for the askable hide below — without a log line, QA has no clue
  // why the whole section is missing from the page.
  useEffect(() => {
    if (!unusable && !askable) {
      console.warn("[AiProductRecommender] hidden — no question is visible before any answer (check show_if chains)");
    }
  }, [unusable, askable]);

  // An engine rejection is an unexpected contract breach (adapter-built catalogs cannot
  // produce one), unlike the authored/known warn states above — log at ERROR level so
  // monitoring sees it, and clear the stored session so a refresh restarts at the banner
  // instead of restoring phase "results" and replaying straight into the same throw.
  // Declared after the persist effect above so the clear wins on the failing render.
  useEffect(() => {
    if (engineFailed) {
      console.error("[AiProductRecommender] hidden — engine rejected the catalog/recipe", engineError);
      clearSession(ssKey);
    }
  }, [engineFailed, engineError, ssKey]);

  if (!renderable) {
    return null;
  }

  const toggleOption = (optId: string) => {
    if (!current) {
      return;
    }
    setAnswers(prev => {
      const sel = prev[current.id] || [];
      const nextSel = current.mode === "single" ?
        [optId] :
        (sel.includes(optId) ? sel.filter(x => x !== optId) : [...sel, optId]);
      return {
        ...prev,
        [current.id]: nextSel
      };
    });
  };

  // AC1 — one pair for both entry points, so they cannot drift. The modal names the quiz's
  // virtual page; the in-page click expands in place, so it reports the host page. linkName is
  // the button's own text, which is the AUTHORED label, so rewritten copy follows the button.
  const entryCtaAttrs = pairWhenMounted(
    entryCtaLabel,
    quizLinkPosition(isModal),
    () => (isModal ? quizVirtualUrl() : currentPageUrl())
  );

  // AC7 — Next, Back and the first step's Close share one POSITION, without the progress token,
  // but each reports under its own name, so the step gets a builder rather than a finished pair.
  const navAttrsFor = (linkName: string) => pairWhenMounted(
    linkName,
    navLinkPosition(isModal, currentQuestionLabel),
    currentPageUrl
  );

  // AC11 / AC12 / Start over all sit on the results screen and share its position.
  const resultsPosition = recommendationLinkPosition(isModal);

  const openModal = () => {
    setModalOpen(true);
  };

  const openInline = () => {
    setInlineStarted(true);
    focusOnOpenRef.current = true;
  };

  const closeModal = () => {
    // Reached from the first step's Close button, which carries AC7's attributes, AND from
    // DtvModal's own X / Esc / backdrop, which cannot: the X belongs to DtvModal. Esc reports
    // nothing at all; the X and the backdrop still reach GTM, but with no position or
    // destination. Tagging DtvModal's root would fix both — WAT-12076's open question.
    //
    // Leaving mid-quiz throws the run away — reopening starts over. A completed run is kept: it
    // is the only state this component ever resumes.
    if (phaseRef.current !== "results") {
      setAnswers({});
      setCursor(0);
      prevCursorRef.current = null;
    }
    setModalOpen(false);
  };

  const handleNext = () => {
    // The "a step needs a pick" rule is enforced at the transition too, not only on the
    // CTA's disabled prop: any other route into this handler (a key binding, a Button that
    // renders aria-disabled instead of the native attribute) must not skip a step — nor
    // report AC7/AC8 for one. Intersected like QuestionStep's canNext, so a stale id the
    // current recipe no longer offers does not count as a pick — but this gate only needs
    // a non-empty intersection; canNext's exactly-one rule for single-select stays at the CTA.
    const sel = (current && answers[current.id]) || [];
    if (!current || current.options.filter(opt => sel.includes(opt.id)).length === 0) {
      return;
    }
    // AC8 — after the AC7 link click, which GTM reports off this button's own attributes and
    // therefore before this handler runs. Its precondition ("clicks Next on an ANSWERED
    // question") holds by construction: the guard above already returned for a pickless step.
    fireQuizQuestionStatus("answered", progressLabel, currentQuestionLabel);
    if (isLast) {
      // Arms AC9. Only this path counts as completing the quiz — see pendingCompleteRef.
      pendingCompleteRef.current = true;
      setPhase("results");
    } else {
      setCursor(clampedCursor + 1);
    }
  };

  const handleBack = () => {
    setCursor(Math.max(0, clampedCursor - 1));
  };

  const handleStartOver = () => {
    clearSession(ssKey);
    // A restored run reached its card without the CTA, so without this the banner would come
    // back instead of Q1.
    if (!isModal) {
      setInlineStarted(true);
    }
    setAnswers({});
    setCursor(0);
    setPhase("ask");
    prevCursorRef.current = null;
  };

  const handleResultCta = (title: string, index: number, price: number | null) => {
    fireGa4ItemList("select_item", [
      {
        item_id: (title || "").toLowerCase(),
        item_name: (title || "").toLowerCase(),
        item_variant: "",
        affiliation: "",
        coupon: "",
        discount: "",
        index,
        item_list_id: "ai product recommender",
        item_list_name: "ai product recommender",
        // The Shop CTA is itself gated on `parts`, so this handler cannot fire for a
        // non-finite price and the "" branch is unreachable through the UI. Kept as the last
        // thing between a partially-migrated feed and a throw inside a click handler, which
        // would take the CTA navigation down with it. Empty string is the documented
        // "unknown price" value.
        price: Number.isFinite(price) ? (price as number).toFixed(2) : "",
        quantity: 1
      }
    ]);
  };

  // Secondary button under a question: Back once past the first step; on the
  // first step it's Close in a modal (dismiss) or nothing inline.
  const stepSecondary = (): SecondaryAction | undefined => {
    if (clampedCursor > 0) {
      return {
        label: backLabel,
        onClick: handleBack
      };
    }
    if (isModal) {
      return {
        label: closeLabel,
        onClick: closeModal
      };
    }
    return undefined;
  };

  const renderQuestion = () => {
    if (!current) {
      return null;
    }
    // Only the NAME varies per option, so the position is built once here rather than once per
    // option inside the builder below.
    const optionPosition = optionLinkPosition(isModal, progressLabel, current.label);
    return (
      <QuestionStep
        blokId={idScope}
        question={current}
        selected={answers[current.id] || []}
        progressText={progressLabel}
        selectHelper={current.mode === "single" ? selectOneLabel : selectAllLabel}
        isLast={isLast}
        nextLabel={nextLabel}
        resultsCtaLabel={resultsCtaLabel}
        secondary={stepSecondary()}
        // AC5/AC6 — the position names the question; only the option the shopper actually
        // clicked is ever read off, which is the ticket's single-select rule.
        optionLinkAttrs={(linkName: string) => pairWhenMounted(linkName, optionPosition, currentPageUrl)}
        navLinkAttrs={navAttrsFor}
        inModal={isModal}
        inpageWide={inpageWide}
        onToggle={toggleOption}
        onNext={handleNext}
      />
    );
  };

  const renderResults = () => {
    if (!result) {
      return null;
    }
    // Authored per pack: the signature card ("broad") and the genre card ("focused") point at
    // different lineups, so each card builds its own from its own url/label pair.
    const secondaryCtaFor = (key: string) => {
      const signature = key === "broad";
      const url = signature ? secondaryCtaSignatureUrl : secondaryCtaGenreUrl;
      if (isModal || !url) {
        return undefined;
      }
      // A fixture or a second adapter must get the same guard the Storyblok adapter gives.
      const checked = secondaryCtaUrl(String(url).trim());
      if (checked.url === null) {
        return undefined;
      }
      const authored = signature ? secondaryCtaSignatureLabel : secondaryCtaGenreLabel;
      const label = String(authored ?? "").trim() || DEFAULT_SECONDARY_CTA_LABEL;
      // AC12 — "see all packages", IN-PAGE ONLY (this returns undefined for the modal, authored
      // or not). Shares AC11's position by the ticket's own wording; the two are told apart by
      // linkName, which GTM takes from each button's text, and by linkDestination.
      return {
        label,
        url: checked.url,
        linkAttrs: linkClickAttrs(label, resultsPosition, absoluteCtaUrl(checked.url))
      };
    };
    return (
      <DtvBox aria-live="polite">
        <DtvBox
          component="h2"
          sx={{ margin: "0 0 16px" }}
        >
          {/* Figma 6976:2333 is Desktop/Subhead/Subhead 1 medium — 36px text-primary.
              `subhead1medium` hits 36px only at 1200-1439px, so the wider tiers are
              pinned back down. */}
          <DtvText
            content={resultsTitle}
            variant="subhead1medium"
            sx={{
              color: colorPalette?.jet?.main,
              ...PIN_36
            }}
          />
        </DtvBox>
        <DtvBox sx={isModal ? RESULTS_ROW_MODAL_SX : RESULTS_ROW_INPAGE_SX}>
          {orderedCards.map((card, i) => (
            <ResultCard
              key={card.key}
              title={card.title}
              price={card.price}
              regularPrice={card.regularPrice}
              promoMonths={card.promoMonths}
              nextTier={card.nextTier}
              promoKnown={card.promoKnown}
              channels={card.channels}
              priceCopy={priceCopy}
              ctaUrl={card.ctaUrl}
              ctaLabel={shopCtaLabel}
              tagLabel={i === 0 ? primaryTagLabel : undefined}
              legal={priceLegal}
              secondaryCta={secondaryCtaFor(card.key)}
              // AC11 — the absolute CCP URL, with every query parameter the recommendation link
              // carries. The DDS helper lowers the reported copy, as it does for every other
              // destination it builds; the `href` below keeps its own case, so the preConfigItems
              // codes the shopper actually navigates with are untouched.
              ctaLinkAttrs={linkClickAttrs(shopCtaLabel, resultsPosition, absoluteCtaUrl(card.ctaUrl))}
              onCtaClick={() => handleResultCta(card.title, i, card.price)}
            />
          ))}
        </DtvBox>
        {/* `result.upsell` is deliberately NOT rendered (2026-07-30 decision). It mixed four
            unrelated claims under the cards — the embedded-streaming inclusion line, the
            premium-movie and sports-depth add-on notes, and an "Also wanted: X" line naming
            packs the engine had just ruled out. That last one directly contradicted the card
            it sat under: sports-"very" removes MySports for having no RSNs, then this list
            recommended MySports by name on the same screen. The field stays on RunResult as
            engine diagnostics — the local configurator renders it next to `trace`. */}
        <DtvBox sx={{ marginTop: "16px" }}>
          <Button
            buttontype="secondary"
            mode="light"
            isTangerine
            size="small"
            leftIcon={<StartOverIcon />}
            // Not one of the twelve ACs, but this control was tracked before the story and
            // dropping it would lose the "restarted instead of converting" signal. It reports at
            // the results position, which is where the button lives.
            //
            // The gate is a no-op here (this screen only exists after an interaction), but do not
            // inline it away: calling currentPageUrl() at the spread makes the SAST engine read
            // document.location as flowing straight into the element, and it fails the build.
            {...pairWhenMounted(startOverLabel, resultsPosition, currentPageUrl)}
            onClick={handleStartOver}
            sx={START_OVER_SX}
          >
            {startOverLabel}
          </Button>
        </DtvBox>
      </DtvBox>
    );
  };

  const quizBody = phase === "results" ? renderResults() : renderQuestion();

  // ----- in-page mode (C): the banner swaps a quiz card into its own box, no overlay -----
  // The banner (and its h2) unmounts when the card opens, so the card keeps the authored heading
  // as a hidden h2 for the outline; the visible copy stays with the progress line. AC-1's centered
  // 992px column (PR #7834 council) is superseded by the phase-2 Figma card (7190:1404).
  if (!isModal) {
    return (
      <DtvBox
        id={id}
        sx={{
          padding: PAGE_GUTTER_PADDING,
          backgroundColor: backgroundColor || "transparent"
        }}
      >
        {inpageOpen ? (
          <DtvBox sx={INPAGE_CARD_SX}>
            {/* The results screen renders its own visible h2; a second one would read twice. */}
            {heading && phase !== "results" && (
              <DtvBox component="h2" sx={SR_ONLY_SX}>
                {heading}
              </DtvBox>
            )}
            {quizBody}
          </DtvBox>
        ) : (
          // No copyMaxWidthPct: the cap is a modal-banner knob; the in-page card uses fixed Figma copy widths.
          <EntryBanner
            heading={heading}
            subhead={subhead}
            entryImage={entryImage}
            entryCtaLabel={entryCtaLabel}
            inpage
            ctaLinkAttrs={entryCtaAttrs}
            onStart={openInline}
          />
        )}
      </DtvBox>
    );
  }

  // ----- modal mode (A, default): banner opens the quiz in a DtvModal -----
  return (
    <DtvBox
      id={id}
      sx={{
        // No horizontal margin here on purpose — see PAGE_GUTTER_PADDING. The section's
        // top/bottom rhythm is WnCMainCanvas' parentSpacers (marginTop only) plus this.
        padding: PAGE_GUTTER_PADDING,
        backgroundColor: backgroundColor || "transparent"
      }}
    >
      <EntryBanner
        heading={heading}
        subhead={subhead}
        entryImage={entryImage}
        entryCtaLabel={entryCtaLabel}
        copyMaxWidthPct={entryCopyMaxWidthPct}
        ctaLinkAttrs={entryCtaAttrs}
        onStart={openModal}
      />
      {/* Mounted only while open. DtvModal registers a window keydown listener with []
          deps and no `open` check (dds.ui.dtv-modal/dist/dtv-modal.js:38-50), so a
          mounted-but-closed modal answered Esc from anywhere on the page and reported a
          close of a modal the user never opened. Keeping it unmounted also keeps its
          MUI Modal/ClickAwayListener/Avatar subtree out of the banner-only render. */}
      {modalOpen && (
        <DtvModal
          open
          handleClose={closeModal}
          modalStyle={MODAL_CARD_SX}
          // Ask and results want different bodies: ask hands its scrolling to an inner region
          // so the CTA row can live outside it, results keeps DtvModal's scrolling body
          // because it has no footer to pin. Two module constants, not an inline object, so
          // this stays one stable reference per phase.
          modalBodyStyle={phase === "results" ? MODAL_BODY_SX : MODAL_BODY_SX_ASK}
          sx={MODAL_ROOT_SX}
          // DtvModal hardcodes aria-label="modal open" and only sets aria-labelledby for
          // CHLUP, so without this a screen reader announces "modal open, dialog". Consumer
          // props are spread last inside DtvModal, so this wins.
          // Spread conditionally rather than passing `|| undefined`: DtvModal's own
          // `"aria-label": "modal open"` sits BEFORE `...otherProps`
          // (dds.ui.dtv-modal/dist/dtv-modal.js:58), so an explicitly-undefined key still
          // overrides it and React then omits the attribute — with both headings blank the
          // dialog would end up with NO accessible name at all. Omitting the key
          // instead lets DtvModal's own label stand as the floor.
          {...(dialogLabel ? { "aria-label": dialogLabel } : {})}
        >
          {/* Ask: a shrinkable column that exactly fills the body, so QuestionStep can split
              itself into a scroll region plus a CTA row. Its 8px of bottom padding stays off —
              the row's own paddingBottom is the gap under the buttons — and its horizontal
              padding moves down into the two regions so the scrollbar keeps the gutter
              BODY_SCROLL_MR aligned it to.
              Results still scrolls as one block and keeps the symmetric 8px. */}
          <DtvBox sx={phase === "results" ? MODAL_CONTENT_RESULTS_SX : MODAL_CONTENT_ASK_SX}>
            {quizBody}
          </DtvBox>
        </DtvModal>
      )}
    </DtvBox>
  );
};

export default AiProductRecommender;
